/*
 * ts-browse-core.js — Shared Typesense browse/search orchestration (no build step).
 *
 * Exposes window.initTsBrowse(cfg). The body is a MECHANICAL EXTRACTION of the
 * giant search-orchestration IIFE that used to live inline in
 * sections/typesense-search.liquid. Every search-specific hardcode is now a cfg
 * field; the DEFAULTS below reproduce today's /search behavior byte-for-byte.
 *
 * Design intent: the same function can later power collection pages by passing a
 * different cfg (mode:'collection', baseFilter, searchBox disabled, routing:'hash',
 * categoryTreeAsNav:false, ssrFallbackSelector, own tracking + empty state). Only
 * SEARCH mode is wired today; collection-only branches are stubbed with TODOs.
 *
 * Rendering is NOT duplicated here — price/spec/money rendering comes from
 * window.TSCore (assets/ts-search-core.js). Connection context (country, B2B,
 * tax map, typesense host/key) comes from window.__tsCtx
 * (snippets/ts-context.liquid).
 *
 * ---------------------------------------------------------------------------
 * cfg CONTRACT (all optional; defaults reproduce /search)
 * ---------------------------------------------------------------------------
 *   mode                'search' | 'collection'                      ('search')
 *   baseFilter          extra filter_by ANDed into EVERY query,      ('')
 *                       for ALL visitors, dealers included.
 *                       search: '' ; collection: e.g.
 *                       'collection_handles:=deye-wechselrichter'
 *   routing             'query' | 'hash' | 'none'                    ('query')
 *                       'query' == InstantSearch routing:true (today).
 *                       'hash'/'none' are STUBS for the collection task.
 *   libsTimeoutMs       CDN-libs wait timeout                        (8000)
 *   cacheSeconds        adapter cacheSearchResultsForSeconds         (120)
 *   nativeSearchPath    error-state native fallback path             ('/search')
 *   indexName           InstantSearch indexName                      (null ->
 *                       __tsCtx.typesense.collection || 'products')
 *   searchParams        { queryBy, queryByWeights, sortBy, numTypos,
 *                         splitJoinTokens }
 *   hitsPerPage         configure() hitsPerPage                      (24)
 *   maxAutoload         auto-load page budget per result set         (2)
 *   manualBatchPages    pages loaded per manual "Mehr laden" click   (2)
 *   searchBox           { enabled, autofocus, searchAsYouType,
 *                         placeholder, prefillParam }  collection: enabled:false
 *   categoryTreeAsNav   tree acts as primary category NAV            (true)
 *                       collection: false (already scoped to a collection)
 *   showCategoryTree    register the category-tree widgets at all    (true)
 *   contextFacets       category -> [{ attribute, title }]
 *   priceBuckets        [{ label, min, max }]
 *   sortItems           sortBy widget items
 *   tracking            { event, path, searchTermEvent }
 *                       event          = result-click denk event
 *                       searchTermEvent= debounced search-term event (null=off)
 *   ssrFallbackSelector selector hidden after first successful render (null)
 *   revealSelector      selector un-hidden on first successful render   (null)
 *                       symmetric to ssrFallbackSelector: the two fire
 *                       together so the SSR grid hides and the TS UI
 *                       reveals atomically. search passes null -> no-op.
 *   renderEmptyState    () => html for the zero-result state
 *   ids                 host element ids (see defaults)
 *   selectors           layout/page/topbar/results selectors
 *   card                Liquid-derived render settings — MUST be passed from the
 *                       section (a static .js cannot evaluate {% %}):
 *                       { showDiscount, showSoldOutBadge, soldOutBadgeLabel,
 *                         imageClass }
 */
(function () {
  'use strict';

  if (window.initTsBrowse) return;

  // Filter-Klausel fuer JEDE Typesense-Abfrage dieses Moduls = baseFilter
  // (Collection-Scope) oder '' (kein Filter), fuer ALLE Besucher inkl.
  // Haendler. Zwillings-Rueckbau 19.10.2026: der fruehere Zusatz
  // `b2b_only:false` (I103, BA-41) ist entfallen, alle B2B-Zwillinge sind
  // geloescht. isB2B (__tsCtx) steuert nur das Preis-Rendering (TSCore).
  // SSoT der Klausel: TSCore.filterBy (ts-search-core.js); Fallback identisch.
  function tsFilterClause(baseFilter) {
    if (window.TSCore && typeof window.TSCore.filterBy === 'function') {
      return window.TSCore.filterBy(baseFilter || '');
    }
    return baseFilter ? String(baseFilter) : '';
  }

  // Klausel in jeden InstantSearch-Request (search + searchForFacetValues)
  // injizieren. Done here (not in additionalSearchParameters.filter_by) because
  // the adapter does not reliably apply filter_by as a base filter across all
  // facet states. Joiner MUSS Typesense `&&` sein, NICHT Algolia `AND`: der
  // typesense-instantsearch-adapter reicht `params.filters` ROH als Typesense
  // `filter_by` durch (keine Algolia->Typesense-Uebersetzung; "... AND ..." =
  // ungueltiges Typesense -> 0 Treffer).
  // Ohne Klausel (Suchseite ohne baseFilter) bleibt jeder Request wie er ist,
  // also kein leeres `filters` und keine Klammer um ein Refinement.
  function tsInjectFilters(requests, baseFilter) {
    var clause = tsFilterClause(baseFilter);
    return requests.map(function(req) {
      if (!clause) return Object.assign({}, req, { params: Object.assign({}, req.params) });
      var f = req.params && req.params.filters;
      return Object.assign({}, req, {
        params: Object.assign({}, req.params, {
          filters: f ? '(' + f + ') && ' + clause : clause,
        }),
      });
    });
  }

  function withDefaults(cfg) {
    cfg = cfg || {};
    function merge(defaults, override) {
      var out = {};
      for (var k in defaults) { if (Object.prototype.hasOwnProperty.call(defaults, k)) out[k] = defaults[k]; }
      if (override) { for (var k2 in override) { if (Object.prototype.hasOwnProperty.call(override, k2)) out[k2] = override[k2]; } }
      return out;
    }

    var D = {
      mode: 'search',
      baseFilter: '',
      routing: 'query',
      libsTimeoutMs: 8000,
      cacheSeconds: 120,
      nativeSearchPath: '/search',
      indexName: null,
      hitsPerPage: 24,
      maxAutoload: 2,
      manualBatchPages: 2,
      categoryTreeAsNav: true,
      showCategoryTree: true,
      ssrFallbackSelector: null,
      headerCountSelector: null,
      revealSelector: null,

      searchParams: {
        queryBy: 'title,short_description,search_keywords,sku,ean,manufacturer_sku,brand,search_tags',
        queryByWeights: '10,5,6,8,6,8,4,2',
        sortBy: '_text_match:desc,popularity_score:desc',
        numTypos: '2,2,2,0,0,0,2,2',
        splitJoinTokens: 'always',
        // Teilstring-Suche auf SKU-Feldern (Index products_v7 hat infix:true auf sku+manufacturer_sku)
        infix: 'off,off,off,always,off,always,off,off'
      },

      searchBox: {
        enabled: true,
        autofocus: true,
        searchAsYouType: true,
        placeholder: 'Produkte, Marken, SKU suchen...',
        prefillParam: 'q'
      },

      tracking: {
        event: 'search_result_click',
        path: '/search',
        searchTermEvent: 'search'
      },

      ids: {
        hits: 'ts-hits',
        searchbox: 'ts-searchbox',
        stats: 'ts-stats',
        filterCategory: 'ts-filter-category',
        filterBrand: 'ts-filter-brand',
        filterPrice: 'ts-filter-price',
        filterDelivery: 'ts-filter-delivery',
        contextFacets: 'ts-context-facets',
        filterCtxPrefix: 'ts-filter-ctx-',
        sortBy: 'ts-sort-by',
        activeFilters: 'ts-active-filters',
        clearFilters: 'ts-clear-filters',
        pagination: 'ts-pagination',
        filtersPanel: 'ts-search-filters-panel',
        filterToggle: 'ts-filter-toggle',
        filterActiveCount: 'ts-filter-active-count',
        mobileReset: 'ts-mobile-reset',
        hitsSkeleton: 'ts-hits-skeleton'
      },

      selectors: {
        layout: '.ts-search-layout',
        page: '.ts-search-page',
        topbar: '.ts-search-topbar',
        results: '.ts-search-results'
      },

      // Kontextuelle Facetten (konfigurativ erweiterbar). Nach dem v5-Reindex hier
      // einfach ergaenzen, z.B. 'Wechselrichter': [{ attribute: 'phases', ... }].
      contextFacets: {
        'Montagesysteme': [
          { attribute: 'material', title: 'Material' }
        ]
      },

      priceBuckets: [
        { label: 'bis 50', min: null, max: 50 },
        { label: '50-100', min: 50, max: 100 },
        { label: '100-250', min: 100, max: 250 },
        { label: '250-500', min: 250, max: 500 },
        { label: '500-1.000', min: 500, max: 1000 },
        { label: 'über 1.000', min: 1000, max: null }
      ],

      sortItems: [
        { label: 'Relevanz', value: 'products' },
        { label: 'Beliebtheit', value: 'products/sort/popularity_score:desc' },
        { label: 'Preis aufsteigend', value: 'products/sort/price:asc' },
        { label: 'Preis absteigend', value: 'products/sort/price:desc' },
        { label: 'Lieferzeit', value: 'products/sort/delivery_days:asc,popularity_score:desc' }
      ],

      // Search-default zero-result state (collection passes its own).
      // Handles hier bewusst cutover-fest: solar-pv und off-grid-spezial werden
      // mit dem Menue-Cutover A6 unveroeffentlicht (22.09.) und sind ersetzt durch
      // pv-module / wechselrichter / stromspeicher, die vor UND nach dem Cutover
      // existieren und gefuellt sind.
      renderEmptyState: function () {
        return '<div class="ts-no-results">'
          + '<h3>Keine Produkte gefunden</h3>'
          + '<p>Bitte prüfen Sie die Schreibweise, oder entfernen Sie einzelne Filter. Vielleicht werden Sie in unseren Kategorien fündig:</p>'
          + '<div class="ts-no-results-links">'
          + '<a href="/collections/pv-module">PV Module</a>'
          + '<a href="/collections/wechselrichter">Wechselrichter</a>'
          + '<a href="/collections/stromspeicher">Stromspeicher</a>'
          + '<a href="/collections/montagesysteme">Montagesysteme</a>'
          + '<a href="/collections/anlagenschutz">Anlagenschutz und Sicherheit</a>'
          + '<a href="/collections/kabel-stecker">Kabel und Stecker</a>'
          + '<a href="/collections/elektroinstallation">Elektroinstallation</a>'
          + '</div>'
          + '<a class="ts-no-results-contact" href="/pages/kontakt">Nicht gefunden, was Sie suchen? Kontaktieren Sie uns, wir helfen gerne weiter.</a>'
          + '</div>';
      },

      card: {
        showDiscount: true,
        showSoldOutBadge: true,
        soldOutBadgeLabel: 'Ausverkauft',
        imageClass: 'product-card__image product-card__image--primary aspect-square'
      }
    };

    var merged = merge(D, cfg);
    merged.ids = merge(D.ids, cfg.ids);
    merged.selectors = merge(D.selectors, cfg.selectors);
    merged.searchBox = merge(D.searchBox, cfg.searchBox);
    merged.searchParams = merge(D.searchParams, cfg.searchParams);
    merged.tracking = merge(D.tracking, cfg.tracking);
    merged.card = merge(D.card, cfg.card);
    return merged;
  }

  // ===========================================================================
  // initTsBrowse — the former sections/typesense-search.liquid page IIFE body.
  // ===========================================================================
  window.initTsBrowse = function initTsBrowse(rawCfg) {
    var cfg = withDefaults(rawCfg);
    var ID = cfg.ids;
    var SEL = cfg.selectors;
    var card = cfg.card;

    var TS_CFG = (window.__tsCtx || {}).typesense || {};

    // Skeletons SOFORT zeigen (kein Blank-Flash und kein Layout-Sprung, bis
    // InstantSearch das erste Ergebnis rendert). Karten-Masse entsprechen den
    // echten Produktkarten. Gilt fuer beide Viewports.
    (function primeSkeletons() {
      // Trefferraster: eigenes Skeleton-Element als Geschwister VOR #ts-hits,
      // damit InstantSearch beim Mounten nichts ueberschreibt. Wird beim ersten
      // Render mit Ergebnissen entfernt.
      var hits = document.getElementById(ID.hits);
      if (hits && !document.getElementById(ID.hitsSkeleton)) {
        var one = '<div class="ts-skel-card">'
          + '<div class="ts-skel-img"></div>'
          + '<div class="ts-skel-line ts-skel-line--sm"></div>'
          + '<div class="ts-skel-line ts-skel-line--lg"></div>'
          + '<div class="ts-skel-line ts-skel-line--md"></div>'
          + '<div class="ts-skel-price"></div>'
          + '</div>';
        var cards = ''; for (var i = 0; i < 12; i++) cards += one;
        var sk = document.createElement('div');
        sk.id = ID.hitsSkeleton;
        sk.className = 'ts-skeleton-grid';
        sk.innerHTML = cards;
        hits.parentNode.insertBefore(sk, hits);
      }
      // Facettenliste (Kategorie): leichtes Skeleton im eigenen Container, das
      // paintTree beim ersten Render ersetzt.
      var cat = document.getElementById(ID.filterCategory);
      if (cat && !cat.children.length) {
        var rows = ''; for (var j = 0; j < 6; j++) rows += '<div class="ts-skel-facet-row"></div>';
        cat.innerHTML = '<div class="ts-facet-header"><span class="ts-facet-title">Kategorie</span></div>'
          + '<div class="ts-facet-body">' + rows + '</div>';
      }
    })();

    // Sticky-Topbar-Hoehe (Suchleiste + Trefferzahl) als CSS-Variable messen,
    // damit Sortierung/Sidebar exakt DARUNTER kleben. Der Header-Offset selbst
    // kommt aus der theme-eigenen, reaktiven --sticky-area-height (beruecksichtigt
    // enable_sticky + Announcement-Bar) -> kein eigenes Header-Messen noetig.
    // Nur auf Desktop im CSS genutzt; auf Mobile gesetzt, aber ungenutzt.
    (function stickyTopbar() {
      var root = document.documentElement;
      var topbar = document.querySelector(SEL.topbar);
      function measureTopbar() {
        if (topbar) root.style.setProperty('--ts-topbar-height', Math.max(0, Math.round(topbar.getBoundingClientRect().height)) + 'px');
      }
      measureTopbar();
      window.addEventListener('load', measureTopbar);
      window.addEventListener('resize', measureTopbar);
      if (window.ResizeObserver && topbar) new ResizeObserver(measureTopbar).observe(topbar);
    })();

    // Gesamter Sticky-Offset (Header-Sticky-Bereich + Topbar) fuer den
    // Auto-Scroll-Zielpunkt. --sticky-area-height ist ein calc()-Ausdruck und via
    // getComputedStyle nicht als Zahl lesbar -> den Header hier direkt messen;
    // --ts-topbar-height ist ein echter px-Wert (per JS gesetzt).
    function stickyScrollOffset() {
      var headerH = 0;
      var sh = document.querySelector('store-header') || document.querySelector('.shopify-section--header');
      if (sh) {
        var pos = window.getComputedStyle(sh).position;
        if (sh.hasAttribute('sticky') || pos === 'sticky' || pos === 'fixed') {
          headerH = sh.getBoundingClientRect().height;
        }
      }
      var t = parseInt(window.getComputedStyle(document.documentElement).getPropertyValue('--ts-topbar-height'), 10) || 0;
      return (headerH + t) || 80; // Fallback 80px
    }

    // Sanft an den Anfang der Ergebnisliste scrollen (zur Trefferzahl), damit der
    // Nutzer nach einer Filteraenderung wieder bei den Produkten landet und nicht
    // unterhalb eines geschrumpften Rasters. Im IIFE-Scope, damit Render-Handler
    // UND Overlay-Controller darauf zugreifen.
    // WICHTIG: NUR nach OBEN scrollen, nie nach unten. Tippt der Nutzer oben in
    // die Suchleiste (Query-Refinement), darf die Seite nicht nach unten zu
    // #ts-stats springen (das versteckte sonst die Suchleiste hinter dem Sticky-
    // Header). Nur scrollen, wenn das Ziel deutlich OBERHALB der aktuellen
    // Position liegt.
    function scrollToResultsTop() {
      // WICHTIG (Shrink-Fall): Schrumpft die Trefferliste, entfernt der Reconciler
      // DOM-Knoten, die Seitenhoehe kollabiert und der Browser klemmt scrollY auf
      // das neue (kurze) Maximum = Footer. Ein SOFORTIGER Scroll (oder eine
      // smooth-Animation) waehrend/vor diesem Reflow wird von diesem Clamp
      // ueberschrieben -> Nutzer landet im Footer. Darum ERST nach dem Reflow +
      // Clamp scrollen (doppeltes requestAnimationFrame) und INSTANT ('auto'):
      // dann ist unser Sprung das letzte Wort und die smooth-Animation kann nicht
      // mehr zerrissen werden (Korrektheit vor Optik).
      // Anker = .ts-search-layout (NICHT sticky) -> echte Dokumentposition der
      // Ergebnisliste. #ts-stats waere falsch, da es (im Sticky-Topbar) beim
      // Scrollen fixiert ist und getBoundingClientRect die Viewport-Position
      // statt der Flussposition liefern wuerde.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          var anchor = document.querySelector(SEL.layout) || document.querySelector(SEL.page);
          if (!anchor) return;
          var targetY = Math.max(0, anchor.getBoundingClientRect().top + window.pageYOffset - stickyScrollOffset());
          if (targetY < window.pageYOffset - 4) { // nur nach oben (4px Toleranz)
            window.scrollTo({ top: targetY, behavior: 'auto' });
          }
        });
      });
    }

    function removeHitsSkeleton() {
      var sk = document.getElementById(ID.hitsSkeleton);
      if (sk && sk.parentNode) sk.parentNode.removeChild(sk);
    }

    // Fehler-/Fallback-Zustand im Trefferbereich: Meldung + "Erneut versuchen"
    // (onRetry) + Link zur nativen Shopify-Suche. Auf IIFE-Ebene, damit auch der
    // Libs-Timeout (bevor "search" existiert) ihn nutzen kann. Query kommt aus der
    // laufenden Instanz oder faellt auf ?q= zurueck.
    function buildErrorState(onRetry) {
      removeHitsSkeleton();
      var container = document.getElementById(ID.hits);
      if (!container) return;
      var q = '';
      try { q = (window.__tsSearchInstance && window.__tsSearchInstance.helper && window.__tsSearchInstance.helper.state.query || '').trim(); } catch (e) {}
      if (!q) { try { q = (new URLSearchParams(window.location.search).get('q') || '').trim(); } catch (e2) {} }
      var href = q ? cfg.nativeSearchPath + '?q=' + encodeURIComponent(q) + '&type=product' : cfg.nativeSearchPath;
      container.innerHTML = '<div class="ts-search-error" role="alert">'
        + '<h3>Die Suche ist gerade nicht verfügbar.</h3>'
        + '<p>Bitte versuchen Sie es erneut oder nutzen Sie unsere Standardsuche.</p>'
        + '<div class="ts-search-error-actions">'
        +   '<button type="button" class="ts-search-retry">Erneut versuchen</button>'
        +   '<a class="ts-search-native" href="' + href + '">Zur Standardsuche</a>'
        + '</div></div>';
      var retry = container.querySelector('.ts-search-retry');
      if (retry) retry.addEventListener('click', function () { if (onRetry) onRetry(); });
    }

    var LIBS_TIMEOUT_MS = cfg.libsTimeoutMs;
    function waitForLibs(cb) {
      var waited = 0;
      (function poll() {
        if (window.TypesenseInstantSearchAdapter && window.instantsearch && window.TSCore) { cb(); return; }
        waited += 50;
        if (waited >= LIBS_TIMEOUT_MS) {
          // CDN-Libs kamen nie an -> Skeleton weg, Fehlerzustand (Retry = Reload).
          buildErrorState(function () { window.location.reload(); });
          return;
        }
        setTimeout(poll, 50);
      })();
    }

    waitForLibs(function() {
      var escapeHTML = window.TSCore.escapeHTML;
      var renderPriceHtml = window.TSCore.renderPriceHtml;

      // Pre-fill from Shopify's ?q= URL param on first load.
      // routing:true handles InstantSearch's own URL format; initialUiState handles Shopify's ?q=.
      var urlParams = new URLSearchParams(window.location.search);
      var initialQuery = (cfg.searchBox.enabled && cfg.searchBox.prefillParam)
        ? (urlParams.get(cfg.searchBox.prefillParam) || '')
        : '';

      var typesenseAdapter = new TypesenseInstantSearchAdapter({
        server: {
          nodes: [{
            host: TS_CFG.host,
            port: TS_CFG.port,
            protocol: TS_CFG.protocol,
          }],
          apiKey: TS_CFG.searchOnlyKey,
          cacheSearchResultsForSeconds: cfg.cacheSeconds,
        },
        additionalSearchParameters: {
          query_by: cfg.searchParams.queryBy,
          query_by_weights: cfg.searchParams.queryByWeights,
          sort_by: cfg.searchParams.sortBy,
          num_typos: cfg.searchParams.numTypos,
          split_join_tokens: cfg.searchParams.splitJoinTokens,
          infix: cfg.searchParams.infix,
        },
      });

      // Wrap searchClient: baseFilter (collection scope) in EVERY query, fuer
      // alle Besucher (siehe tsInjectFilters oben).
      var _baseClient = typesenseAdapter.searchClient;
      function injectB2B(requests) {
        return tsInjectFilters(requests, cfg.baseFilter);
      }
      // Fehlgeschlagene Requests duerfen nicht im Client-Cache bleiben: der
      // typesense-js-Client legt das PROMISE je Request fuer
      // cacheSearchResultsForSeconds (120 s) ab, auch wenn es abgelehnt wird.
      // Jede Wiederholung derselben Seite (Auto-Retry, "Erneut versuchen")
      // bekaeme sonst 2 Minuten lang sofort denselben Fehler ohne Netzwerk.
      function dropFailedFromCache(err) {
        try { typesenseAdapter.clearCache(); } catch (e) {}
        throw err;
      }
      var searchClient = {
        search: function(requests) {
          return _baseClient.search(injectB2B(requests)).catch(dropFailedFromCache);
        },
        // WICHTIG: Ohne diese Delegation faellt die Facettensuche (searchable
        // refinementList, z.B. "Marke suchen...") aus, weil der Wrapper sonst
        // nur search() haette. Der baseFilter wird auch hier injiziert.
        searchForFacetValues: function(requests) {
          if (typeof _baseClient.searchForFacetValues !== 'function') return Promise.resolve([]);
          return _baseClient.searchForFacetValues(injectB2B(requests)).catch(dropFailedFromCache);
        },
      };

      // Routing: 'query' == today's InstantSearch routing:true (state in the URL
      // query string, SEARCH behaviour — UNCHANGED). 'none' == no URL sync.
      // 'hash' == genuine hash routing (collection): filter/sort state is encoded
      // ONLY into location.hash (#f=...), NEVER into location.search — so no
      // indexable ?query-param URL / crawl-trap is ever produced.
      //
      // buildHashRouting() returns an InstantSearch { router, stateMapping } that
      // reuses the stock history router but overrides createURL/parseURL to
      // read/write the hash instead of the search string. If the InstantSearch
      // build does not expose the history router / singleIndex mapping (defensive),
      // it falls back to routingOption=false (no URL sync) — NEVER to query params.
      // Einstieg mit #page=N (Neuladen, geteilter Link): connectInfiniteHits
      // holte nur Seite N (z. B. 5 Karten) und meldete isLastPage, die Leiste
      // zeigte "Alle 53 geladen". Beim ersten Lesen der URL wird page darum
      // verworfen, ausser bei Zurueck/Vor mit gespeichertem Hits-Cache fuer
      // diesen Pfad (dann kommen alle Seiten aus dem Cache). Spaetere
      // URL-Aenderungen (popstate) bleiben unberuehrt.
      function initialPageGuard(mapping, idxName) {
        var first = true;
        function keepPage() {
          try {
            var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
            if (!nav || nav.type !== 'back_forward') return false;
            var saved = JSON.parse(window.sessionStorage.getItem('ts.infiniteHits') || 'null');
            return !!(saved && saved.sig && saved.sig.indexOf(window.location.pathname + '|') === 0);
          } catch (e) { return false; }
        }
        return Object.assign({}, mapping, {
          routeToState: function (route) {
            var ui = mapping.routeToState(route);
            if (first) {
              first = false;
              try {
                var u = ui && ui[idxName];
                if (u && u.page && !keepPage()) {
                  var copy = Object.assign({}, u);
                  delete copy.page;
                  ui = Object.assign({}, ui);
                  ui[idxName] = copy;
                }
              } catch (e) {}
            }
            return ui;
          },
        });
      }

      function buildHashRouting() {
        try {
          var routers = instantsearch.routers || {};
          var mappings = instantsearch.stateMappings || {};
          if (typeof routers.history !== 'function' || typeof mappings.singleIndex !== 'function') {
            return false; // no safe hash router available -> 'none'
          }
          var idxName = cfg.indexName || TS_CFG.collection || 'products';
          return {
            router: routers.history({
              getLocation: function () { return window.location; },
              // Write state into the HASH; keep pathname + existing search untouched.
              createURL: function (args) {
                var qs = args.qsModule.stringify(args.routeState);
                var loc = args.location;
                var base = loc.pathname + (loc.search || '');
                return base + (qs ? '#' + qs : '');
              },
              // Read state from the HASH (NOT from location.search).
              parseURL: function (args) {
                var hash = (args.location.hash || '').replace(/^#/, '');
                return args.qsModule.parse(hash);
              },
            }),
            stateMapping: initialPageGuard(mappings.singleIndex(idxName), idxName),
          };
        } catch (e) {
          return false; // defensive: never emit query-param URLs
        }
      }

      var routingOption;
      switch (cfg.routing) {
        case 'query':
          routingOption = true;
          break;
        case 'hash':
          routingOption = buildHashRouting(); // {router,stateMapping} or false (->none)
          break;
        case 'none':
          routingOption = false;
          break;
        default:
          routingOption = true;
      }

      var search = instantsearch({
        indexName: cfg.indexName || TS_CFG.collection || 'products',
        searchClient: searchClient,
        routing: routingOption,
        initialUiState: initialQuery ? { products: { query: initialQuery } } : undefined,
      });
      // Fuer den (ausserhalb dieses Callbacks laufenden) Overlay-Controller
      window.__tsSearchInstance = search;

      // ---- Facet-Panels: nie leere Facetten anzeigen --------------------------
      // Jede Facette steckt in einem Panel, das sich versteckt, sobald es fuer
      // die aktuelle Treffermenge keine Optionen gibt (plus optionale Kontext-
      // Bedingung). Ueberschriften kommen aus dem Panel-Header, nicht aus dem
      // Markup, damit sie mit verschwinden.
      function facetPanel(title, extraHiddenFn) {
        return instantsearch.widgets.panel({
          templates: { header: '<span class="ts-facet-title">' + title + '</span>' },
          hidden: function(options) {
            // Leer-Ausblendung. In COLLECTION-Mode wird zusaetzlich eine
            // 1-Wert-Facette ausgeblendet: unter dem collection-baseFilter spiegeln
            // die Live-Werte bereits nur die Collection-Produkte, ein einziger Wert
            // ist also entweder der Scope der Collection selbst (z.B. Marke "Deye"
            // auf einer Deye-Collection) oder ein nutzloser Filter. /search bleibt
            // EXAKT bei `=== 0` (minItems=0 -> `<= 0` == `=== 0`, byte-identisch).
            var minItems = cfg.mode === 'collection' ? 1 : 0;
            if (!options.items || options.items.length <= minItems) return true;
            return extraHiddenFn ? !!extraHiddenFn(options) : false;
          },
          cssClasses: { root: 'ts-facet-panel', header: 'ts-facet-header', body: 'ts-facet-body' },
        });
      }

      function refinedCategories(results) {
        try {
          return (results.getRefinements() || [])
            .filter(function(r) { return r.attributeName === 'category'; })
            .map(function(r) { return r.name; });
        } catch (e) { return []; }
      }

      function categoryShare(results, name) {
        try {
          var facets = results.disjunctiveFacets || [];
          for (var i = 0; i < facets.length; i++) {
            if (facets[i].name === 'category') {
              var count = (facets[i].data || {})[name] || 0;
              return results.nbHits ? count / results.nbHits : 0;
            }
          }
        } catch (e) {}
        return 0;
      }

      // Kontext aktiv, wenn die Kategorie explizit verfeinert ist ODER (ohne
      // Kategorie-Verfeinerung) klar dominiert (>60 % der Treffer).
      function contextActive(results, categoryName) {
        if (!results) return false;
        var refined = refinedCategories(results);
        if (refined.length) return refined.indexOf(categoryName) !== -1;
        return categoryShare(results, categoryName) > 0.6;
      }

      // Standard-Sortierung fuer refinementList-Facetten: ausgewaehlte Werte oben
      // angepinnt (nie unter "Mehr anzeigen" versteckt) + ab dem ersten Render
      // stabile count:desc,name:asc-Ordnung (kein Umsortier-Sprung beim Tick).
      // Gilt fuer Marke, Material und ALLE Kontext-Facetten (siehe Factory unten).
      // NICHT fuer Lieferzeit (feste semantische Reihenfolge) und NICHT fuer den
      // Unterkategorie-Baum (bewusst eingefroren ohne isRefined).
      var DEFAULT_REFINEMENT_SORT = ['isRefined', 'count:desc', 'name:asc'];

      // ---- Kontextuelle Facetten (konfigurativ erweiterbar) --------------------
      var CONTEXT_FACETS = cfg.contextFacets;

      var contextWidgets = [];
      var ctxHost = document.getElementById(ID.contextFacets);
      Object.keys(CONTEXT_FACETS).forEach(function(categoryName) {
        CONTEXT_FACETS[categoryName].forEach(function(fc) {
          var el = document.createElement('div');
          el.id = ID.filterCtxPrefix + fc.attribute;
          ctxHost.appendChild(el);
          contextWidgets.push(
            facetPanel(fc.title, function(options) {
              return !contextActive(options.results, categoryName);
            })(instantsearch.widgets.refinementList)({
              container: '#' + el.id,
              attribute: fc.attribute,
              // Pin+stabil fuer ALLE (auch kuenftige) Kontext-Facetten by default
              sortBy: DEFAULT_REFINEMENT_SORT,
              limit: 8,
              showMore: true,
              templates: { showMoreText: function(data) { return data.isShowingMore ? 'Weniger anzeigen' : 'Mehr anzeigen'; } },
            })
          );
        });
      });

      // ---- Kategorie-Baum (category + subcategory, ohne Schema-Aenderung) ------
      // Ein Widget mit Baum-UX aus den bestehenden Facetten category (7) +
      // subcategory (26). Kategorie = Einfachauswahl (Drilldown blendet die
      // anderen Kategorien aus, mit Zurueck-Affordanz), Unterkategorie =
      // Mehrfachauswahl. Beide Refinements laufen ueber die echten Facetten,
      // daher bleiben Routing UND CONTEXT_FACETS (keyt auf refined category)
      // unveraendert. Zwei connectRefinementList-Connectoren schreiben in einen
      // gemeinsamen Zustand und rendern EINEN Container.
      // NOTE (collection): cfg.categoryTreeAsNav flags whether the tree is the
      // primary category nav (search) vs. deprioritized (collection). Whether the
      // widgets are registered at all is cfg.showCategoryTree (see addWidgets).
      // subsubcategory (dritte Ebene) ist der Ausweichpfad fuer Kategorien, deren
      // Kinder nach dem Kategorie-Cutover eine Ebene tiefer liegen: category =
      // Wurzel ("Elektro & Werkzeug"), subcategory = die Collection selbst
      // ("Kabel & Stecker") und damit nur EIN Facettenwert, die eigentliche
      // Auswahl steht in subsubcategory. Ohne diesen Zweig verlieren
      // kabel-stecker, anlagenschutz und werkzeug ihren Unterkategorie-Filter.
      var treeState = { category: null, subcategory: null, subsubcategory: null, wired: false, subsStale: false, catVal: undefined };
      var catHasSub = {}; // Kategoriename -> true, wenn Unterkategorien existieren
      var allCategoryValues = []; // ALLE 7 Kategorien (stabile Reihenfolge, aus precompute)

      function refinedValue(opts) {
        if (!opts || !opts.items) return null;
        for (var i = 0; i < opts.items.length; i++) { if (opts.items[i].isRefined) return opts.items[i].value; }
        return null;
      }

      function treeBack() {
        if (treeState.category) {
          (treeState.category.items || []).forEach(function(i) { if (i.isRefined) treeState.category.refine(i.value); });
        }
        if (treeState.subcategory) {
          (treeState.subcategory.items || []).forEach(function(i) { if (i.isRefined) treeState.subcategory.refine(i.value); });
        }
        if (treeState.subsubcategory) {
          (treeState.subsubcategory.items || []).forEach(function(i) { if (i.isRefined) treeState.subsubcategory.refine(i.value); });
        }
      }

      // Zeilen einer Ebene-Liste. level ist 2 (subcategory) oder 3
      // (subsubcategory) und landet als data-sub bzw. data-subsub am Button.
      function treeSubRows(items, level) {
        var attr = level === 3 ? 'data-subsub' : 'data-sub';
        return items.map(function(s) {
          return '<button type="button" class="ts-tree-row ts-tree-sub' + (s.isRefined ? ' is-refined' : '') + '" ' + attr + '="' + escapeHTML(s.value) + '" aria-pressed="' + (s.isRefined ? 'true' : 'false') + '">'
            + '<span class="ts-tree-check" aria-hidden="true"></span>'
            + '<span class="ts-tree-label">' + escapeHTML(s.label) + '</span>'
            + '<span class="ts-tree-count">' + s.count + '</span>'
            + '</button>';
        }).join('');
      }

      // Welche Ebene zeigt der Filter? Hat subcategory weniger als zwei echte
      // Optionen, aber subsubcategory mindestens zwei, schaltet der Baum auf die
      // dritte Ebene um. Rueckgabe: { items, level } oder null.
      function treeUsableSubs(subs) {
        if (subs && subs.length >= 2) return { items: subs, level: 2 };
        var deep = (treeState.subsubcategory && treeState.subsubcategory.items) || [];
        if (deep.length >= 2) return { items: deep, level: 3 };
        return null;
      }

      function categoryChevron() {
        return '<svg class="ts-tree-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 6l6 6-6 6"/></svg>';
      }

      // Delegierten Klick-Listener EINMAL am (ueber Renders stabilen) Tree-Host
      // verdrahten. Ausgelagert, damit sowohl der normale Baum als auch der
      // Collection-Single-Category-Zweig (der frueher via return() rendert) ihn
      // nutzen. data-sub laeuft in BEIDEN Faellen; data-cat/data-back gibt es nur
      // im normalen Baum.
      function ensureTreeWired(host) {
        if (treeState.wired) return;
        treeState.wired = true;
        host.addEventListener('click', function(e) {
          var el = e.target.closest ? e.target.closest('[data-cat],[data-sub],[data-subsub],[data-back]') : null;
          if (!el) return;
          // Bei Kategoriewechsel/Zurueck: die aktuell gerenderten Unterkategorien
          // SOFORT (bei der Interaktion, vor der neuen Suche) aus dem DOM nehmen,
          // damit nie ein Frame mit den Kindern der ALTEN Kategorie gemalt wird.
          if (el.hasAttribute('data-cat') || el.hasAttribute('data-back')) {
            treeState.subsStale = true;
            var subsEl = host.querySelector('.ts-tree-subs');
            if (subsEl && subsEl.parentNode) subsEl.parentNode.removeChild(subsEl);
          }
          if (el.hasAttribute('data-back')) { treeBack(); return; }
          if (el.hasAttribute('data-cat') && treeState.category) { treeState.category.refine(el.getAttribute('data-cat')); return; }
          if (el.hasAttribute('data-sub') && treeState.subcategory) { treeState.subcategory.refine(el.getAttribute('data-sub')); return; }
          if (el.hasAttribute('data-subsub') && treeState.subsubcategory) { treeState.subsubcategory.refine(el.getAttribute('data-subsub')); }
        });
      }

      function paintTree() {
        var host = document.getElementById(ID.filterCategory);
        if (!host || !treeState.category) return;
        var cats = treeState.category.items || [];
        var refinedCat = null;
        for (var i = 0; i < cats.length; i++) { if (cats[i].isRefined) { refinedCat = cats[i]; break; } }

        // ---- COLLECTION-MODE: datengetriebenes Kategorie-Scoping (gated) --------
        // Die implizite Kategorie der Collection wird direkt aus der (per
        // baseFilter gescopeten) category-Facette gelesen. Genau EINE Kategorie
        // => die Collection IST diese Kategorie: den redundanten Kategorie-Root/
        // Drilldown weglassen und stattdessen die Unterkategorien DIREKT als
        // Refinement-Liste zeigen. Diese sind unter baseFilter bereits
        // collection-scoped (disjunktives Faceting der subcategory-Facette liefert
        // sie OHNE Kategorie-Verfeinerung -> KEIN Auto-Refine, also keine crawlbare
        // URL, kein "Kategorie:"-Chip, kein Reset-Bruch, keine Schleife). Keine
        // Unterkategorien => Baum ganz ausblenden (leerer Host). >=2 Kategorien
        // (Marken-/Multi-Kategorie-Collection) ODER categoryTreeAsNav (`all`)
        // => normaler Baum unten, UNVERAENDERT (Fall d).
        if (cfg.mode === 'collection' && !cfg.categoryTreeAsNav && !refinedCat) {
          // Stabile precompute-Liste bevorzugen (unabhaengig von aktiven Preis-/
          // Marken-/Lieferzeit-Filtern); vor Abschluss des Precompute Fallback auf
          // die Live-Facette.
          var collCatVals = allCategoryValues.length ? allCategoryValues : cats.map(function(c) { return c.value; });
          if (collCatVals.length === 1) {
            // Subkategorie-Connector noch nicht gefeuert -> Skeleton stehen lassen
            // (er rendert unmittelbar nach dem Kategorie-Connector im selben Render-
            // Zyklus). NICHT den Host leeren -> unterscheidet "Subs noch nicht
            // geladen" von "echte Blattkategorie ohne Subs".
            if (!treeState.subcategory) return;
            var subsC = treeState.subsStale ? [] : (treeState.subcategory.items || []);
            // Kategorie-Filter NUR zeigen, wenn er >=2 echte Optionen bietet:
            // 0 Subs = Blattkategorie; 1 Sub = die Collection selbst als einzige
            // Unterkategorie (z.B. /collections/pv-module -> subcategory nur
            // "PV Module") = keine echte Auswahl -> Baum ganz weg. Name-unabhaengig,
            // future-proof (>=2 Subkategorien = Top-Kategorie-Collection wie
            // solar-pv/montagesysteme -> Drilldown sinnvoll).
            // Weniger als zwei Unterkategorien: auf die dritte Ebene ausweichen
            // (siehe Kommentar an treeState). Bietet auch die nichts, bleibt es
            // beim leeren Host.
            var usableC = treeUsableSubs(subsC);
            if (!usableC) { host.innerHTML = ''; return; }
            var subRowsC = treeSubRows(usableC.items, usableC.level);
            host.innerHTML = '<div class="ts-facet-header"><span class="ts-facet-title">Kategorie</span></div>'
              + '<div class="ts-facet-body ts-category-tree">'
              +   '<div class="ts-tree-subs">' + subRowsC + '</div>'
              + '</div>';
            ensureTreeWired(host);
            return;
          }
          // >=2 Kategorien -> normaler Baum unten (unveraendert).
        }

        if (!cats.length && !refinedCat) { host.innerHTML = ''; return; }

        var body;
        if (!refinedCat) {
          // Top-Level: IMMER alle Kategorien zeigen (stabile Reihenfolge). Nur die
          // 7 Hauptkategorien sind hier von der Leer-Ausblendung ausgenommen:
          // Kategorien ohne Treffer unter den aktuellen Filtern werden ausgegraut
          // + deaktiviert, NICHT entfernt. Unterkategorien/andere Facetten blenden
          // Leere weiterhin aus.
          var byVal = {};
          cats.forEach(function(c) { byVal[c.value] = c; });
          var order = allCategoryValues.length ? allCategoryValues : cats.map(function(c) { return c.value; });
          body = order.map(function(val) {
            var c = byVal[val];
            var count = c ? c.count : 0;
            var disabled = !c || count === 0;
            var drillable = !disabled && catHasSub[val] !== false; // deaktiviert => kein Drill
            return '<button type="button" class="ts-tree-row ts-tree-cat' + (drillable ? '' : ' ts-tree-leaf') + (disabled ? ' is-disabled' : '') + '"'
              + (disabled ? ' disabled aria-disabled="true"' : '') + ' data-cat="' + escapeHTML(val) + '">'
              + '<span class="ts-tree-label">' + escapeHTML(val) + '</span>'
              + '<span class="ts-tree-meta"><span class="ts-tree-count">' + count + '</span>'
              + (drillable ? categoryChevron() : '')
              + '</span>'
              + '</button>';
          }).join('');
        } else {
          // Beim Kategoriewechsel sind die Unterkategorien der ALTEN Kategorie
          // veraltet -> subsStale unterdrueckt sie synchron (kein Stale-Flash).
          var subs = treeState.subsStale ? [] : ((treeState.subcategory && treeState.subcategory.items) || []);
          var usable = treeState.subsStale ? null : treeUsableSubs(subs);
          var subRows = usable ? treeSubRows(usable.items, usable.level) : '';
          // Blattkategorie oder (noch) keine Unterkategorien: nur Zurueck + Titel,
          // NIE ein "keine Unterteilung"-Leerbild.
          body = '<button type="button" class="ts-tree-back" data-back="1">‹ Alle Kategorien</button>'
            + '<div class="ts-tree-current">' + escapeHTML(refinedCat.label) + '</div>'
            + (subRows ? '<div class="ts-tree-subs">' + subRows + '</div>' : '');
        }

        host.innerHTML = '<div class="ts-facet-header"><span class="ts-facet-title">Kategorie</span></div>'
          + '<div class="ts-facet-body ts-category-tree">' + body + '</div>';

        ensureTreeWired(host);
      }

      var categoryTreeWidget = instantsearch.connectors.connectRefinementList(function(opts) {
        var nv = refinedValue(opts);
        if (nv !== treeState.catVal) {
          // Kategorie gewechselt: Unterkategorien der alten Elternkategorie
          // sofort aus der Anzeige nehmen, bis der Unterkategorie-Connector
          // frisch (auf die neue Kategorie gescoped) nachrendert.
          treeState.catVal = nv;
          treeState.subsStale = true;
        }
        treeState.category = opts;
        paintTree();
      });
      var subcategoryTreeWidget = instantsearch.connectors.connectRefinementList(function(opts) {
        treeState.subcategory = opts;
        treeState.subsStale = false;
        paintTree();
      });
      var subsubcategoryTreeWidget = instantsearch.connectors.connectRefinementList(function(opts) {
        treeState.subsubcategory = opts;
        paintTree();
      });

      // Kategorie -> hat-Unterkategorien vorab bestimmen (7 kleine Facet-Queries
      // per multi_search), damit kinderlose Kategorien als Blatt (ohne Chevron)
      // erscheinen. Laufzeit-Fallback greift ohnehin (leere Sub-Liste => nur
      // Zurueck + Titel, kein Leerbild).
      // baseFilter (collection scope) ueber dieselbe Klausel wie alle anderen
      // Abfragen (tsFilterClause); leer = kein filter_by.
      function tsBaseFilter() {
        return tsFilterClause(cfg.baseFilter);
      }

      function precomputeCatHasSub() {
        var coll = TS_CFG.collection || 'products';
        var base = tsBaseFilter();
        var p = new URLSearchParams({ q: '*', query_by: 'title', facet_by: 'category', per_page: '0', max_facet_values: '50' });
        if (base) p.set('filter_by', base);
        fetch('https://' + TS_CFG.host + '/collections/' + coll + '/documents/search?' + p, { headers: { 'X-TYPESENSE-API-KEY': TS_CFG.searchOnlyKey } })
          .then(function(r) { return r.json(); })
          .then(function(d) {
            var fc = (d.facet_counts || []).filter(function(f) { return f.field_name === 'category'; })[0];
            var cats = fc ? fc.counts.map(function(c) { return c.value; }) : [];
            if (!cats.length) return;
            // Stabile, vollstaendige Kategorienliste (unabhaengig von spaeteren
            // Filtern) -> Top-Level zeigt immer alle Kategorien.
            allCategoryValues = cats.slice();
            paintTree();
            var searches = cats.map(function(cat) {
              var f = 'category:=`' + cat + '`' + (base ? ' && ' + base : '');
              return { collection: coll, q: '*', query_by: 'title', filter_by: f, facet_by: 'subcategory', per_page: 0, max_facet_values: 1 };
            });
            return fetch('https://' + TS_CFG.host + '/multi_search', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-TYPESENSE-API-KEY': TS_CFG.searchOnlyKey },
              body: JSON.stringify({ searches: searches })
            }).then(function(r) { return r.json(); }).then(function(res) {
              (res.results || []).forEach(function(rr, idx) {
                var sfc = (rr.facet_counts || []).filter(function(f) { return f.field_name === 'subcategory'; })[0];
                catHasSub[cats[idx]] = !!(sfc && sfc.counts && sfc.counts.length);
              });
              paintTree();
            });
          })
          .catch(function() {});
      }

      // ---- Preis-Widget: Min/Max-Eingaben + Bucket-Chips (connectRange) --------
      // Der Index filtert NETTO-EUR; Eingaben/Buckets laufen in der ANZEIGE-Welt
      // (Brutto bzw. CHF) und werden via TSCore.displayToNetto zurueckgerechnet.
      var priceClear = function() {};

      var PRICE_BUCKETS = cfg.priceBuckets;

      function renderPriceWidget(renderOptions, isFirstRender) {
        var container = document.querySelector('#' + ID.filterPrice);
        if (!container) return;
        var refine = renderOptions.refine;
        var start = renderOptions.start || [];
        var D = window.TSCore;

        priceClear = function() { refine([undefined, undefined]); };

        function readNetto(idx) {
          var v = start[idx];
          return (typeof v === 'number' && isFinite(v)) ? v : null;
        }

        if (isFirstRender) {
          var dc = D.displayContext();
          var sym = dc.currency === 'EUR' ? '€' : dc.currency;
          container.innerHTML = ''
            + '<div class="ts-facet-header ts-price-header"><span class="ts-facet-title">Preis</span></div>'
            + '<div class="ts-facet-body">'
            +   '<div class="ts-price-inputs">'
            +     '<input type="number" inputmode="numeric" min="0" class="ts-price-input" data-bound="min" placeholder="Min" aria-label="Mindestpreis in ' + sym + '">'
            +     '<span class="ts-price-sep">bis</span>'
            +     '<input type="number" inputmode="numeric" min="0" class="ts-price-input" data-bound="max" placeholder="Max" aria-label="Höchstpreis in ' + sym + '">'
            +     '<button type="button" class="ts-price-apply">OK</button>'
            +   '</div>'
            +   '<div class="ts-price-buckets">'
            +   PRICE_BUCKETS.map(function(b, i) {
                  return '<button type="button" class="ts-price-bucket" data-bucket="' + i + '">' + b.label + ' ' + sym + '</button>';
                }).join('')
            +   '</div>'
            + '</div>';

          function applyInputs() {
            var minEl = container.querySelector('[data-bound="min"]');
            var maxEl = container.querySelector('[data-bound="max"]');
            var lo = minEl.value === '' ? undefined : D.displayToNetto(parseFloat(minEl.value));
            var hi = maxEl.value === '' ? undefined : D.displayToNetto(parseFloat(maxEl.value));
            if (lo != null && !isFinite(lo)) lo = undefined;
            if (hi != null && !isFinite(hi)) hi = undefined;
            refine([lo, hi]);
          }

          container.querySelector('.ts-price-apply').addEventListener('click', applyInputs);
          container.querySelectorAll('.ts-price-input').forEach(function(inp) {
            inp.addEventListener('keydown', function(e) { if (e.key === 'Enter') { e.preventDefault(); applyInputs(); } });
          });
          container.querySelectorAll('.ts-price-bucket').forEach(function(btn) {
            btn.addEventListener('click', function() {
              var b = PRICE_BUCKETS[parseInt(btn.getAttribute('data-bucket'), 10)];
              var lo = b.min != null ? D.displayToNetto(b.min) : undefined;
              var hi = b.max != null ? D.displayToNetto(b.max) : undefined;
              if (btn.classList.contains('is-active')) { refine([undefined, undefined]); return; }
              refine([lo, hi]);
            });
          });
        }

        // Eingaben mit aktuellem Zustand synchronisieren (Anzeige-Welt, ganze
        // Betraege) — aber nie ein Feld ueberschreiben, in dem gerade getippt wird.
        var loNetto = readNetto(0);
        var hiNetto = readNetto(1);
        var minInput = container.querySelector('[data-bound="min"]');
        var maxInput = container.querySelector('[data-bound="max"]');
        if (minInput && document.activeElement !== minInput) {
          minInput.value = loNetto != null ? String(Math.round(D.nettoToDisplay(loNetto))) : '';
        }
        if (maxInput && document.activeElement !== maxInput) {
          maxInput.value = hiNetto != null ? String(Math.round(D.nettoToDisplay(hiNetto))) : '';
        }

        // Aktiven Bucket markieren (Toleranz 1 in der Anzeige-Welt)
        var loDisp = loNetto != null ? D.nettoToDisplay(loNetto) : null;
        var hiDisp = hiNetto != null ? D.nettoToDisplay(hiNetto) : null;
        container.querySelectorAll('.ts-price-bucket').forEach(function(btn) {
          var b = PRICE_BUCKETS[parseInt(btn.getAttribute('data-bucket'), 10)];
          var matchLo = b.min == null ? loDisp == null : (loDisp != null && Math.abs(loDisp - b.min) <= 1);
          var matchHi = b.max == null ? hiDisp == null : (hiDisp != null && Math.abs(hiDisp - b.max) <= 1);
          btn.classList.toggle('is-active', matchLo && matchHi);
        });
      }

      var customPriceWidget = instantsearch.connectors.connectRange(renderPriceWidget);

      // ---- Aktive Filter als entfernbare Chips (connectCurrentRefinements) -----
      var ATTR_LABELS = {
        category: 'Kategorie',
        subcategory: 'Unterkategorie',
        subsubcategory: 'Unterkategorie',
        brand: 'Marke',
        availability: 'Lieferzeit',
        material: 'Material',
      };
      var AVAIL_CHIP_LABELS = { sofort: 'Auf Lager', bestellbar: 'Bestellbar', ausverkauft: 'Ausverkauft' };

      function renderActiveChips(renderOptions) {
        var container = document.getElementById(ID.activeFilters);
        if (!container) return;
        var items = renderOptions.items || [];
        var D = window.TSCore;
        var chips = [];

        items.forEach(function(group) {
          if (group.attribute === 'price') {
            var lo = null, hi = null;
            group.refinements.forEach(function(r) {
              if (r.operator === '>=') lo = r.value;
              if (r.operator === '<=') hi = r.value;
            });
            var fmt = function(v) { return D.formatDisplayAmount(D.nettoToDisplay(v)); };
            var label = (lo != null && hi != null) ? fmt(lo) + ' bis ' + fmt(hi)
              : lo != null ? 'ab ' + fmt(lo)
              : hi != null ? 'bis ' + fmt(hi) : '';
            if (label) chips.push({ label: 'Preis: ' + label, action: function() { if (priceClear) priceClear(); } });
          } else {
            group.refinements.forEach(function(r) {
              var val = group.attribute === 'availability' ? (AVAIL_CHIP_LABELS[r.label] || r.label) : r.label;
              var name = ATTR_LABELS[group.attribute] || group.attribute;
              chips.push({ label: name + ': ' + val, action: function() { group.refine(r); } });
            });
          }
        });

        container.hidden = chips.length === 0;
        container.innerHTML = chips.map(function(c, i) {
          return '<button type="button" class="ts-active-chip" data-chip="' + i + '">'
            + window.TSCore.escapeHTML(c.label)
            + '<span class="ts-active-chip-x" aria-hidden="true">&times;</span>'
            + '<span class="sr-only">Filter entfernen</span>'
            + '</button>';
        }).join('');
        container.querySelectorAll('.ts-active-chip').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var c = chips[parseInt(btn.getAttribute('data-chip'), 10)];
            if (c) c.action();
          });
        });
      }

      var activeChipsWidget = instantsearch.connectors.connectCurrentRefinements(renderActiveChips);

      // ---- Trefferliste mit keyed Reconciliation (connectHits) -----------------
      // Behebt das Bild-Flackern: bleibt ein Produkt ueber Renders erhalten
      // (gleiche id UND gleiche image_url), behaelt es SEINEN DOM-Knoten samt
      // <img> (kein Remount). Nur wirklich geaenderte Karten werden neu gebaut,
      // entfernt oder umsortiert. Markup/Discount/Preis/Verfuegbarkeit/Spec-Chips
      // sind identisch zur bisherigen Vorlage; denk.track laeuft weiter ueber den
      // delegierten Klick-Listener auf #ts-hits.
      // Unified availability (card contract slot f) — identical structure +
      // classes + 14px SVG dots as the native card (product-card.liquid):
      //   <div class="product-card__availability">svg + <span ...-label></div>
      // TS-only extras: "· {delivery_label}" <small> and the low-stock line
      // (data only exists in the index); "LIEFERBAR AB {datum}" is SSR-only.
      function stockHTMLFor(hit) {
        var avail = hit.availability || (hit.in_stock ? 'sofort' : 'ausverkauft');
        var deliveryLabel = hit.delivery_label || '';
        var suffix = deliveryLabel ? ' <small>· ' + escapeHTML(deliveryLabel) + '</small>' : '';
        var availHTML;
        if (avail === 'sofort') {
          availHTML = '<div class="product-card__availability">'
            + '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="#2BC932"/><path d="M6.72281 11L4 8.28169L4.71579 7.5493L6.72281 9.56338L11.2842 5L12 5.71831L6.72281 11Z" fill="#FAFAFC"/></svg>'
            + '<span class="product-card__availability-label">AUF LAGER' + suffix + '</span>'
            + '</div>';
        } else if (avail === 'bestellbar') {
          availHTML = '<div class="product-card__availability">'
            + '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="#FFCC00"/><path d="M8 4V8.5L10.5 10" stroke="#FAFAFC" stroke-width="1.5" stroke-linecap="round"/></svg>'
            + '<span class="product-card__availability-label">BESTELLBAR' + suffix + '</span>'
            + '</div>';
        } else {
          availHTML = '<div class="product-card__availability">'
            + '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="#E74C3C"/><path d="M5 5L11 11M5 11L11 5" stroke="#FAFAFC" stroke-width="1.5"/></svg>'
            + '<span class="product-card__availability-label">AUSVERKAUFT</span>'
            + '</div>';
        }

        // Low-Stock-Dringlichkeit: NUR wenn echt auf Lager UND knapp (<= 5),
        // nie fuer Nachbestellung/ausverkauft (stock 0). Dezente Zeile darunter.
        var urgency = '';
        var q = hit.stock_quantity;
        if (hit.in_stock === true && typeof q === 'number' && q > 0 && q <= 5) {
          urgency = '<span class="product-card__lowstock">Nur noch ' + q + ' auf Lager</span>';
        }

        return availHTML + urgency;
      }

      // XSS-Haerten: den Typesense-Highlight-Wert NICHT roh injizieren. Nur
      // <mark>/</mark> als Markup zulassen, den restlichen Titeltext escapen.
      function safeHighlightTitle(hit) {
        var raw = (hit._highlightResult && hit._highlightResult.title && hit._highlightResult.title.value);
        if (!raw) return escapeHTML(hit.title || '');
        // Nur <mark>/</mark> als Markup zulassen: maskieren -> alles escapen ->
        // Marken zurueck. Platzhalter (Steuerzeichen) kommen in Titeln nie vor.
        var OPEN = String.fromCharCode(1), CLOSE = String.fromCharCode(2);
        var masked = String(raw).split('<mark>').join(OPEN).split('</mark>').join(CLOSE);
        return escapeHTML(masked).split(OPEN).join('<mark>').split(CLOSE).join('</mark>');
      }

      function renderCardHTML(hit) {
        // UNIFIED CARD CONTRACT: same inner markup as the native SSR card
        // (snippets/product-card.liquid). Wrapper exception: native uses
        // <product-card> (web component, SSR-only), the TS card uses a plain
        // <div class="product-card ts-card"> — NOT an <a>, nested anchors would
        // be invalid; clickability comes from the inner figure/title links,
        // exactly like the native card.
        // Rabatt nur bei echtem positivem Preis (price 0 => "Preis auf Anfrage",
        // kein 100%-Fehlrabatt). Karten haben KEINE Beschreibung mehr
        // (ts-description entfernt — Vertrag: Marke/Titel/Chips/Preis/Verfuegbarkeit).
        var brand = escapeHTML(hit.brand || '');
        var specChips = window.TSCore.buildSpecChips(hit);
        var highlightedTitle = safeHighlightTitle(hit);
        var priceHtml = hit.price != null ? renderPriceHtml(hit.price, hit.compare_at_price || null) : '';
        // P7 (BA-13): Haendler-Karte mit Kategorie-/Produktstaffeln -> SSR-Preis
        // stehen lassen statt ihn mit Kunden-% zu ueberschreiben.
        var ssrPriceHtml = window.TSCore.ssrB2bPriceHtml ? window.TSCore.ssrB2bPriceHtml(hit.handle) : null;
        if (ssrPriceHtml !== null) priceHtml = ssrPriceHtml;
        var url = '/products/' + escapeHTML(hit.handle || '');

        // Unified badge slot: exactly ONE badge top-left, priority
        // discount > merchandising > sold_out (mirrors product-badges.liquid
        // single mode). The Liquid settings (show_discount / show_sold_out_badge)
        // + the sold-out label are passed in via cfg.card (a static .js cannot
        // evaluate {% %}); values reproduce the section settings 1:1.
        var badge = '';
        if (card.showDiscount) {
          badge = hit.price != null ? window.TSCore.discountBadgeHTML(hit.price, hit.compare_at_price || null) : '';
        }
        if (card.showSoldOutBadge) {
          if (!badge) {
            var availState = hit.availability || (hit.in_stock ? 'sofort' : 'ausverkauft');
            if (availState === 'ausverkauft') {
              badge = '<span class="badge badge--sold-out">' + card.soldOutBadgeLabel + '</span>';
            }
          }
        }
        var badgeList = badge
          ? '<div class="product-card__badge-list product-labels__grid">' + badge + '</div>'
          : '';

        // Figure: like native — only rendered when there is an image (the native
        // card skips the whole product-card__figure block when media.size == 0).
        // srcset via Shopify-CDN width rewriting; aspect class mirrors the
        // native image_tag classes (cfg.card.imageClass = the Liquid-computed
        // "product-card__image ... aspect-<ratio>" attribute value).
        var figure = '';
        if (hit.image_url) {
          var srcset = window.TSCore.imageSrcset(hit.image_url, [200, 400, 600, 800]);
          figure = '<div class="product-card__figure">'
            + '<a href="' + url + '" data-instant>'
            + '<img src="' + escapeHTML(hit.image_url) + '"'
            + (srcset ? ' srcset="' + escapeHTML(srcset) + '" sizes="(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 300px"' : '')
            + ' alt="' + escapeHTML(hit.title || '') + '" loading="lazy"'
            + ' class="' + card.imageClass + '">'
            + '</a>'
            + badgeList
            + '</div>';
        }

        // Info block: same slot order + classes as the native card
        // (product-card.liquid): vendor -> v-stack(title, specs, price,
        // availability). SSR-only extras the TS card deliberately does NOT
        // render: rating, quick-buy, swatches (B2B-Preislogik sitzt in TSCore
        // via __tsCtx) and the <price-list> wrapper element.
        return '<div class="product-card ts-card" data-handle="' + escapeHTML(hit.handle || '') + '">'
          + figure
          + '<div class="product-card__info">'
          + (brand ? '<span class="product-card__vendor link-faded text-xs">' + brand + '</span>' : '')
          + '<div class="v-stack gap-0.5 w-full">'
          + '<span class="product-card__title"><a class="bold" href="' + url + '" data-instant>' + highlightedTitle + '</a></span>'
          + specChips
          + priceHtml
          + stockHTMLFor(hit)
          + '</div>'
          + '</div>'
          + '</div>';
      }

      function hitsEmptyHTML() {
        return cfg.renderEmptyState();
      }

      function hitKey(hit) {
        return String(hit.objectID != null ? hit.objectID : (hit.id != null ? hit.id : (hit.handle || '')));
      }

      // "Mehr laden"-Zustand (connectInfiniteHits liefert die AKKUMULIERTE
      // Trefferliste ueber alle geladenen Seiten). Der Reconciler behaelt alle
      // bereits gerenderten Karten (gleiche id+image_url) und HAENGT neue Seiten
      // nur an -> kein Neuaufbau, kein Bild-Flackern.
      var infState = { showMore: null, showPrevious: null, isFirst: true, isLast: true, loaded: 0, total: 0, page: 0, nbPages: 0, perPage: 24 };
      var loadMorePending = false;
      var loadMoreObserver = null;
      // Footer erreichbar halten: pro Trefferliste laden wir hoechstens so viele
      // Seiten automatisch (IntersectionObserver) nach; danach nur noch per Klick.
      // autoLoadCount wird bei jeder neuen Suche/Verfeinerung auf 0 zurueckgesetzt.
      var MAX_AUTOLOAD = cfg.maxAutoload;
      var autoLoadCount = 0;
      // Manueller "Mehr laden"-Klick laedt einen FESTEN Stapel von 48 Produkten
      // (= 2 Seiten bei hitsPerPage 24). Fest statt Verdopplung: vorhersehbar,
      // performant (Verdopplung wuerde das DOM nach wenigen Klicks auf 500-1000+
      // Karten aufblaehen) und 48 fuellt volle Reihen auf Desktop (4 Spalten) wie
      // Mobile (2 Spalten). Tunbar ueber diese eine Konstante.
      var MANUAL_BATCH_PAGES = cfg.manualBatchPages; // 2 x 24 = 48 Produkte pro Klick
      // Der Stapel wird als Kette von showMore()-Aufrufen ueber die Renders
      // geladen (jede Seite wartet auf ihren Render -> keine Luecken, keyed
      // Reconciliation intakt). pendingBatchPages = noch ausstehende Seiten der
      // Kette; batchBusy = Ladezustand des Buttons.
      var pendingBatchPages = 0;
      var batchBusy = false;
      // InstantSearch rendert auch beim START einer Suche (status 'loading') und
      // bei haengender Suche ('stalled'), jeweils mit den ALTEN Ergebnissen.
      // connectInfiniteHits legt eine Seite nur im Status 'idle' in seinen Cache.
      // Loest ein solcher Zwischen-Render das naechste showMore() aus, fliegen
      // zwei Seiten gleichzeitig und die erste kommt nie in den Cache (z. B.
      // /collections/deye: Seite 2 fehlte, 29 von 53 Karten, "Alle 53 geladen").
      // Darum gilt ein Nachladen erst als fertig und das naechste darf erst
      // starten, wenn die Instanz wieder 'idle' ist.
      function searchIdle() {
        return !search.status || search.status === 'idle';
      }
      // Fehlerzustand (Typesense-Fehler/Timeout); wird beim naechsten erfolgreichen
      // Render geloescht.
      var searchErrored = false;
      // Nachladen fehlgeschlagen (z. B. Typesense 429/5xx auf Seite N+1 bzw.
      // auf einer Vorseite der showPrevious-Kette). Nach dem Fehler setzt
      // InstantSearch den Helper beim Fehler-Render auf den letzten gueltigen
      // State zurueck (lastValidSearchParameters); helper.search() holte darum
      // die LETZTE GUTE Seite erneut, nicht die fehlende. showMore()/
      // showPrevious() rechnen dagegen aus dem Seiten-Cache (letzte/erste
      // erhaltene Seite +/- 1) und treffen genau die fehlende Seite.
      // Wiederholt wird deshalb die Aktion, die fehlschlug (lastLoadAction).
      // Ein Fehler-Episode endet erst bei echtem Fortschritt (mehr Karten als
      // beim Fehler) oder einer neuen Suche (Signatur ohne page); innerhalb
      // einer Episode hoechstens RETRY_MAX automatische Wiederholungen
      // (1/2/4/8 s + Jitter), danach nur per Klick mit sichtbarer Meldung.
      // Solange loadMoreFailed gilt, pausieren Auto-Load und Render-Ketten.
      var loadMoreFailed = false;
      var retryTimer = null;
      var retryCount = 0; // automatische Wiederholungen der laufenden Episode
      var RETRY_MAX = 4; // 1 s, 2 s, 4 s, 8 s (+ Jitter), dann nur per Klick
      var lastLoadAction = 'more'; // 'more' (showMore) | 'prev' (showPrevious)
      var failAction = 'more';
      var failSig = null; // Suchsignatur der Episode
      var failLoaded = 0; // Kartenzahl beim (ersten) Fehler der Episode
      // Harte Grenze fuer die showPrevious-Kette je Trefferliste (Signatur),
      // unabhaengig von Fehlern: ein Einstieg auf Seite N braucht N-1 Schritte.
      var PREV_CHAIN_MAX = 20;
      var prevChainCount = 0;
      var prevChainSig = null;
      // Signatur der Trefferliste ohne page, reihenfolgeunabhaengig (der
      // Fehler-Render ersetzt den Helper-State durch results._state, Schluessel-
      // reihenfolgen koennen dabei wechseln, vgl. B1a).
      function episodeSig(st) {
        if (!st) return '';
        function c(v) {
          if (Array.isArray(v)) return v.map(function (x) { return String(c(x)); }).sort();
          if (v && typeof v === 'object') {
            var o = {};
            Object.keys(v).sort().forEach(function (k) {
              if (v[k] === undefined || (Array.isArray(v[k]) && !v[k].length)) return;
              o[k] = c(v[k]);
            });
            return o;
          }
          return v;
        }
        try {
          return JSON.stringify(c({
            q: st.query || '', idx: st.index || '',
            d: st.disjunctiveFacetsRefinements || {}, f: st.facetsRefinements || {},
            h: st.hierarchicalFacetsRefinements || {}, num: st.numericRefinements || {},
          }));
        } catch (e) { return ''; }
      }
      function currentEpisodeSig() { return episodeSig(search.helper && search.helper.state); }
      // showMore/showPrevious ausloesen und merken, welche Aktion lief.
      // fn optional (Render-Ketten uebergeben die zum Zeitpunkt des Renders
      // gueltige Funktion, wie bisher).
      function runLoad(action, fn) {
        fn = fn || (action === 'prev' ? infState.showPrevious : infState.showMore);
        if (!fn) { loadMorePending = false; return; }
        lastLoadAction = action;
        fn();
      }
      function retryLoadMore() {
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
        var fn = failAction === 'prev' ? infState.showPrevious : infState.showMore;
        if (!fn) return;
        loadMorePending = true;
        try { updateLoadMoreUI(); } catch (e) {}
        try { runLoad(failAction, fn); } catch (e) { loadMorePending = false; try { updateLoadMoreUI(); } catch (e2) {} }
      }
      function scheduleLoadMoreRetry() {
        if (retryTimer) return;
        if (retryCount >= RETRY_MAX) { batchBusy = false; try { updateLoadMoreUI(); } catch (e) {} return; }
        var delay = Math.min(8000, 1000 * Math.pow(2, retryCount)) + Math.floor(Math.random() * 400);
        retryCount++;
        retryTimer = setTimeout(function () {
          retryTimer = null;
          if (!loadMoreFailed) return;
          // Neue Suche (Sortierung/Filter/Query) seit dem Fehler: Episode
          // verwerfen, die neue Liste laedt selbst.
          if (failSig !== currentEpisodeSig()) {
            clearLoadMoreFailure();
            try { updateLoadMoreUI(); } catch (e) {}
            return;
          }
          retryLoadMore();
        }, delay);
      }
      function noteLoadMoreFailure() {
        var sig = currentEpisodeSig();
        if (!loadMoreFailed || failSig !== sig) {
          // neue Episode
          retryCount = 0;
          failSig = sig;
          failLoaded = infState.loaded;
        }
        failAction = lastLoadAction;
        loadMoreFailed = true;
      }
      function clearLoadMoreFailure() {
        loadMoreFailed = false;
        retryCount = 0;
        failSig = null;
        if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      }
      // Automatische Wiederholungen erschoepft, kein Request und kein Timer
      // offen: sichtbare Meldung, Button bleibt bedienbar.
      function loadMoreGaveUp() {
        return loadMoreFailed && !loadMorePending && !retryTimer && retryCount >= RETRY_MAX;
      }
      function loadMoreBusy() {
        return batchBusy || (loadMoreFailed && (loadMorePending || !!retryTimer));
      }

      // SSR-Fallback (collection): nach dem ersten erfolgreichen Render die
      // serverseitige Basisliste ausblenden. Fuer /search ist ssrFallbackSelector
      // null -> No-op.
      var ssrFallbackHidden = false;
      var ssrReservedHeight = 0;
      function hideSsrFallbackOnce() {
        if (ssrFallbackHidden || !cfg.ssrFallbackSelector) return;
        var el = document.querySelector(cfg.ssrFallbackSelector);
        if (el) {
          // CLS-Schutz: Hoehe des SSR-Rasters messen, BEVOR es kollabiert, und beim
          // Reveal als min-height der TS-UI reservieren -> der Takeover-Tausch
          // aendert die Gesamt-Seitenhoehe nicht (Footer/Inhalt darunter springen
          // nicht = kein Layout-Shift durch den Wechsel). TS-Raster ist spaltenparitaet-
          // gleich, wird also ~gleich hoch; ist es kuerzer, haelt min-height den Platz.
          ssrReservedHeight = Math.round(el.getBoundingClientRect().height) || 0;
          el.hidden = true;
        }
        ssrFallbackHidden = true;
      }

      // Reveal-Hook (collection): symmetrisch zu hideSsrFallbackOnce. Nach dem
      // ersten erfolgreichen Render die (per [hidden] unsichtbare) TS-UI einblenden,
      // GEMEINSAM mit dem Ausblenden des SSR-Rasters -> atomarer Tausch, kein
      // Doppel-Content-Flash. Fuer /search ist revealSelector null -> No-op.
      var tsUiRevealed = false;
      function revealTsUiOnce() {
        if (tsUiRevealed || !cfg.revealSelector) return;
        var el = document.querySelector(cfg.revealSelector);
        if (el) {
          el.hidden = false; el.removeAttribute('hidden'); el.classList.remove('ts-hidden');
          if (ssrReservedHeight > 0) el.style.minHeight = ssrReservedHeight + 'px';
        }
        tsUiRevealed = true;
      }

      // Takeover rueckgaengig: SSR-Raster wieder zeigen, TS-UI verstecken. Fuer
      // die Fehler-Wiederherstellung (Collection): wenn Typesense NACH bereits
      // erfolgtem Takeover ausfaellt, nicht in einer Fehler-Box stranden, sondern
      // zum (noch vorhandenen) serverseitigen Raster zuruecktauschen.
      function restoreSsrFallback() {
        if (!cfg.ssrFallbackSelector) return;
        var ssr = document.querySelector(cfg.ssrFallbackSelector);
        if (ssr) { ssr.hidden = false; ssr.removeAttribute('hidden'); }
        var ts = cfg.revealSelector && document.querySelector(cfg.revealSelector);
        if (ts) { ts.hidden = true; ts.style.minHeight = ''; }
        ssrFallbackHidden = false;
        tsUiRevealed = false;
      }

      // Aktive Nutzer-Verfeinerung (Query/Facette/Preis)? Entscheidet, ob ein
      // 0-Treffer-Render der TS-Leerzustand ist (nach Filtern) ODER bedeutet, dass
      // die Collection gar nicht im Index vertreten ist (initial, unrefined).
      function hasActiveRefinements() {
        var s = search.helper && search.helper.state;
        if (!s) return false;
        if (s.query && s.query.length) return true;
        var d = s.disjunctiveFacetsRefinements || {};
        if (Object.keys(d).some(function(k){ return (d[k] || []).length; })) return true;
        var f = s.facetsRefinements || {};
        if (Object.keys(f).some(function(k){ return (f[k] || []).length; })) return true;
        var h = s.hierarchicalFacetsRefinements || {};
        if (Object.keys(h).some(function(k){ return (h[k] || []).length; })) return true;
        var n = s.numericRefinements || {};
        if (Object.keys(n).some(function(k){ var m = n[k] || {}; return Object.keys(m).some(function(op){ return (m[op] || []).length; }); })) return true;
        return false;
      }

      function setupLoadMoreUI() {
        var host = document.getElementById(ID.pagination);
        if (!host || host.querySelector('.ts-loadmore')) return;
        host.innerHTML = '<div class="ts-loadmore">'
          + '<div class="ts-loadmore-progress">'
          +   '<span class="ts-loadmore-text" role="status" aria-live="polite"></span>'
          +   '<div class="ts-loadmore-bar"><div class="ts-loadmore-bar-fill"></div></div>'
          + '</div>'
          + '<button type="button" class="ts-loadmore-btn">Mehr laden</button>'
          + '</div>';
        host.querySelector('.ts-loadmore-btn').addEventListener('click', function() {
          // Ein Klick laedt einen FESTEN Stapel (MANUAL_BATCH_PAGES = 48 Produkte),
          // begrenzt auf die noch verbleibenden Seiten. Zweiter Klick waehrend
          // eines laufenden Stapels ODER waehrend eines fliegenden Auto-Loads
          // (loadMorePending) wird ignoriert -> kein doppeltes showMore().
          // Waehrend Laden/Backoff nur aria-disabled (Fokus bleibt am Button):
          // Klick ignorieren wie frueher beim disabled-Button.
          if (loadMoreBusy()) return;
          if (loadMoreFailed) {
            // Fehlgeschlagene Aktion erneut (showMore bzw. showPrevious, siehe
            // oben). Manuell zaehlt nicht gegen RETRY_MAX; nach erschoepften
            // Auto-Wiederholungen bleibt es bei Klicks.
            retryLoadMore();
            return;
          }
          if (batchBusy || loadMorePending || infState.isLast || !infState.showMore || !searchIdle()) return;
          var loadedPages = infState.page + 1;
          var remainingPages = Math.max(0, infState.nbPages - loadedPages);
          var batchPages = Math.min(MANUAL_BATCH_PAGES, remainingPages);
          if (batchPages <= 0) return;
          batchBusy = true;
          pendingBatchPages = batchPages - 1; // erste Seite gleich, Rest via Render-Kette
          loadMorePending = true;
          updateLoadMoreUI();
          runLoad('more');
        });
        // Auto-Load, sobald der Button in Sichtweite kommt - aber nur begrenzt
        // (MAX_AUTOLOAD Seiten pro Trefferliste), damit der Footer erreichbar
        // bleibt. Ist das Budget verbraucht, ignoriert der Observer weitere
        // Intersections; der "Mehr laden"-Button laedt dann NUR noch per Klick
        // (Klicks sind immer erlaubt und re-aktivieren Auto-Load NICHT). Waehrend
        // eines manuellen Stapels (batchBusy) pausiert der Auto-Load.
        if ('IntersectionObserver' in window) {
          loadMoreObserver = new IntersectionObserver(function(entries) {
            entries.forEach(function(e) {
              if (e.isIntersecting && !infState.isLast && !loadMorePending && !batchBusy && infState.showMore
                  && autoLoadCount < MAX_AUTOLOAD && searchIdle() && !loadMoreFailed) {
                autoLoadCount++;
                loadMorePending = true;
                runLoad('more');
              }
            });
          }, { rootMargin: '300px' });
          loadMoreObserver.observe(host.querySelector('.ts-loadmore-btn'));
        }
      }

      // Der IntersectionObserver feuert nur bei einem Sichtbarkeits-WECHSEL. Auf
      // kurzen Seiten bleibt der Button nach dem 1. Auto-Load in Sicht, ohne dass
      // ein neuer Wechsel eintritt -> der 2. Auto-Load kaeme nie. Darum nach jedem
      // Render aktiv nachpruefen: Button noch in Sichtweite (+300px Vorlauf wie der
      // Observer) und Budget/Bedingungen offen -> naechsten Auto-Load ausloesen.
      // Endlos-Schutz: autoLoadCount < MAX_AUTOLOAD und !isLast begrenzen.
      function loadMoreBtnInView() {
        var btn = document.querySelector('.ts-loadmore-btn');
        if (!btn || btn.hidden) return false;
        var r = btn.getBoundingClientRect();
        var vh = window.innerHeight || document.documentElement.clientHeight;
        return r.top < vh + 300 && r.bottom > -300;
      }
      function maybeAutoLoad() {
        if (batchBusy || loadMorePending || infState.isLast || !infState.showMore || !searchIdle() || loadMoreFailed) return;
        if (autoLoadCount >= MAX_AUTOLOAD) return;
        if (!loadMoreBtnInView()) return;
        autoLoadCount++;
        loadMorePending = true;
        runLoad('more');
      }

      function updateLoadMoreUI() {
        var host = document.getElementById(ID.pagination);
        if (!host) return;
        var wrap = host.querySelector('.ts-loadmore');
        if (!wrap) return;
        var textEl = wrap.querySelector('.ts-loadmore-text');
        var fillEl = wrap.querySelector('.ts-loadmore-bar-fill');
        var btn = wrap.querySelector('.ts-loadmore-btn');
        var loaded = infState.loaded, total = infState.total;
        var pct = total > 0 ? Math.min(100, Math.round(loaded / total * 100)) : 0;
        if (fillEl) fillEl.style.width = pct + '%';
        // "Alle N geladen" nur, wenn wirklich alle Karten da sind. isLastPage
        // allein ist nach einem Einstieg mitten in der Liste (#page=N) oder
        // nach einer fehlgeschlagenen letzten Seite wahr, obwohl Karten fehlen.
        var complete = infState.isLast && !loadMoreFailed && loaded >= total;
        var busy = loadMoreBusy();
        var gaveUp = loadMoreGaveUp();
        if (complete) {
          if (textEl) textEl.textContent = 'Alle ' + total + ' Produkt' + (total === 1 ? '' : 'e') + ' geladen';
          if (btn) btn.hidden = true;
        } else {
          if (textEl) {
            textEl.textContent = gaveUp
              ? 'Laden fehlgeschlagen. ' + loaded + ' von ' + total + ' geladen'
              : loaded + ' von ' + total + ' geladen';
          }
          if (btn) {
            // Ohne weitere Seite und ohne Fehler gibt es nichts nachzuladen
            // (die Vorseiten laedt die Render-Kette selbst): Button weg.
            btn.hidden = infState.isLast && !loadMoreFailed;
            // Nie `disabled` (ein deaktivierter Button verliert den Tastatur-
            // Fokus an body): aria-disabled/aria-busy, Klick wird ignoriert.
            if (btn.disabled) btn.disabled = false;
            btn.setAttribute('aria-disabled', busy ? 'true' : 'false');
            btn.setAttribute('aria-busy', busy ? 'true' : 'false');
            btn.classList.toggle('is-loading', busy);
            btn.textContent = busy ? 'Lädt…' : (gaveUp ? 'Erneut versuchen' : 'Mehr laden');
          }
        }
        wrap.hidden = total === 0;
      }

      var hitsNodes = {}; // key -> { node, img }
      function renderHitsWidget(renderOptions) {
        var container = document.getElementById(ID.hits);
        if (!container) return;
        var results = renderOptions.results;
        var hits = renderOptions.hits || [];

        // Render im Fehlerzustand: InstantSearch rendert nach einem Fehler mit
        // den ALTEN Ergebnissen (und mit der fehlgeschlagenen Seite im State,
        // isLastPage kann dann falsch sein). Nichts am Raster aendern: das
        // Fehler-/SSR-Raster bzw. die bereits geladenen Karten bleiben stehen,
        // nur der Lade-Zustand wird aktualisiert.
        if (search.status === 'error') {
          try { updateLoadMoreUI(); } catch (e) {}
          return;
        }

        // Erfolgreicher Render -> etwaigen Fehlerzustand aufheben (das folgende
        // Neurendern von #ts-hits ersetzt die Fehler-DOM ohnehin). Ausserdem den
        // optionalen SSR-Fallback (collection) einmalig ausblenden.
        // ★C1: Takeover NUR aktivieren, wenn die Collection im Index vertreten ist.
        // Eine INITIALE (unrefined) 0-Treffer-Antwort heisst: der Handle ist nicht
        // in collection_handles (manuelle/kuratierte Collection, `all`, brandneu,
        // 0-Produkt-Smart-Collection oder Sync-Luecke) -> das SSR-Raster (mit echten
        // Produkten) NICHT ausblenden, NIE eine leere TS-Seite darueberlegen (sonst
        // indexiert Googlebot eine leere Kategorie auf der Haupt-Ranking-Flaeche).
        // Sobald der Takeover einmal lief ODER der Nutzer aktiv gefiltert hat, gilt
        // der TS-Leerzustand normal (echte 0-Treffer nach Verfeinerung).
        // `!cfg.ssrFallbackSelector` == /search (kein SSR-Fallback) -> Bedingung
        // IMMER wahr -> /search-Verhalten unveraendert (nie unterdrueckt). Die
        // Unterdrueckung greift NUR im Collection-Modus.
        if (results) {
          searchErrored = false;
          if (!cfg.ssrFallbackSelector || tsUiRevealed || results.nbHits > 0 || hasActiveRefinements()) {
            hideSsrFallbackOnce();
            revealTsUiOnce();
          } else {
            return; // Collection nicht im Index -> SSR bleibt sichtbar, TS bleibt hidden
          }
        }

        // Header-Produktzahl (Collection-Banner) auf die B2C-genaue TS-Trefferzahl
        // syncen: die native „N Produkte" = collection.products_count zaehlt auch
        // die versteckten B2B-Duplikat-Produkte mit (z.B. 64), waehrend die B2C-
        // Sicht nur 55 zeigt -> Header und TS-UI wichen ab. NUR im unrefined Zustand
        // (echte Collection-Gesamtzahl fuer B2C, nicht die gefilterte Zahl);
        // #ts-stats zeigt weiterhin die live gefilterte Zahl. Nur Collection-Mode
        // (Selector gesetzt); /search hat keinen headerCountSelector -> No-op.
        if (results && cfg.headerCountSelector && !hasActiveRefinements()) {
          var hcEl = document.querySelector(cfg.headerCountSelector);
          if (hcEl) hcEl.textContent = results.nbHits + ' Produkte';
        }

        // Load-More-Zustand aktualisieren. Ein Zwischen-Render (Suche laeuft
        // noch) beendet das laufende Nachladen NICHT (siehe searchIdle).
        if (searchIdle()) {
          loadMorePending = false;
          // Fehler-Episode nur bei echtem Fortschritt (mehr Karten als beim
          // Fehler) oder bei einer neuen Suche beenden. Ein Render ohne neue
          // Karten setzt die Wiederholungen NICHT zurueck (sonst eskaliert der
          // Backoff nie und RETRY_MAX greift nie).
          if (results && loadMoreFailed
              && (hits.length > failLoaded || failSig !== currentEpisodeSig())) {
            clearLoadMoreFailure();
          }
        }
        infState.showMore = renderOptions.showMore;
        infState.showPrevious = renderOptions.showPrevious;
        infState.isFirst = renderOptions.isFirstPage !== false;
        infState.isLast = !!renderOptions.isLastPage;
        infState.total = results ? results.nbHits : 0;
        infState.loaded = hits.length;
        infState.page = results ? results.page : 0;
        infState.nbPages = results ? results.nbPages : 0;
        infState.perPage = (results && results.hitsPerPage) ? results.hitsPerPage : 24;

        if (results && results.nbHits === 0) {
          container.innerHTML = hitsEmptyHTML();
          hitsNodes = {};
          updateLoadMoreUI();
          return;
        }
        if (!hits.length) return; // noch keine Ergebnisse -> Skeleton bleibt stehen

        var list = container.querySelector('.ts-hits-list');
        if (!list) {
          container.innerHTML = '<div class="ts-hits-list"></div>';
          list = container.querySelector('.ts-hits-list');
          hitsNodes = {};
        } else if (!Object.keys(hitsNodes).length && list.firstChild) {
          // hitsNodes wurde zurueckgesetzt (Fehler, SSR-Rueckfall), die Liste
          // traegt aber noch die alten Karten: leeren, sonst stehen sie doppelt.
          list.innerHTML = '';
        }

        var next = {};
        hits.forEach(function(hit, i) {
          // Leerer Key (weder objectID/id/handle) -> eindeutiger Index-Fallback,
          // damit zwei keylose Treffer nicht auf '' kollidieren.
          var key = hitKey(hit) || ('__ts_idx_' + i);
          var img = hit.image_url || '';
          var prev = hitsNodes[key];
          var node;
          if (prev && prev.img === img) {
            node = prev.node; // unveraendert -> Knoten (und <img>) behalten (kein Flackern)
            // ABER: bei Query-Aenderung ist das <mark>-Highlight veraltet -> nur
            // den Titel auf die aktuelle Query aktualisieren (Karte NICHT neu
            // bauen, damit das <img> nicht neu laedt). Der Titeltext lebt im
            // <a> innerhalb von span.product-card__title (unified contract).
            var titleLink = node.querySelector('.product-card__title a');
            if (titleLink) {
              var fresh = safeHighlightTitle(hit);
              if (titleLink.innerHTML !== fresh) titleLink.innerHTML = fresh;
            }
          } else {
            var tmp = document.createElement('div');
            tmp.innerHTML = renderCardHTML(hit);
            node = tmp.firstChild;
          }
          next[key] = { node: node, img: img };
          list.appendChild(node); // appendChild verschiebt/haengt an -> Reihenfolge = Trefferreihenfolge
        });
        Object.keys(hitsNodes).forEach(function(key) {
          if (!next[key]) {
            var n = hitsNodes[key].node;
            if (n && n.parentNode === list) list.removeChild(n);
          }
        });
        hitsNodes = next;
        updateLoadMoreUI();
      }
      var customHitsWidget = instantsearch.connectors.connectInfiniteHits(renderHitsWidget);

      // Zurueck-Navigation: die URL traegt #page=N, der Standard-Cache von
      // connectInfiniteHits lebt aber nur im Speicher. Nach "Zurueck" kam
      // deshalb nur die letzte Seite (z. B. 5 von 53 Karten). Dieser Cache
      // spiegelt die geladenen Seiten in EINEN sessionStorage-Slot (Pfad +
      // Zustand ohne page) und liest ihn nur bei einer Back/Forward-Navigation
      // wieder ein; jeder andere Aufruf startet frisch (keine alten Preise).
      function createBackNavHitsCache() {
        var KEY = 'ts.infiniteHits';
        var path = window.location.pathname;
        var mem = null; // { sig, hits }
        function store() { try { return window.sessionStorage; } catch (e) { return null; } }
        // Signatur unabhaengig von der Reihenfolge: JSON.stringify haengt an der
        // Schluessel-Reihenfolge, und die unterscheidet sich zwischen Schreiben
        // (disjunctiveFacetsRefinements in Klick-Reihenfolge) und Lesen nach
        // Zurueck (Widget-Reihenfolge aus der URL). Darum Schluessel rekursiv
        // sortieren, leere Refinement-Listen weglassen und Wertelisten von
        // Refinements (Mengen, Reihenfolge ohne Bedeutung) sortieren.
        var SET_KEY = /Refinements$|Excludes$|^disjunctiveFacets$|^facets$/;
        function isEmptyVal(v) {
          if (v == null) return true;
          if (Array.isArray(v)) return v.length === 0;
          if (typeof v === 'object') return Object.keys(v).every(function (k) { return isEmptyVal(v[k]); });
          return false;
        }
        function canon(v, inSet) {
          if (Array.isArray(v)) {
            var arr = v.map(function (x) { return canon(x, inSet); });
            if (inSet && arr.every(function (x) { return x === null || typeof x !== 'object'; })) {
              arr.sort(function (a, b) { a = String(a); b = String(b); return a < b ? -1 : a > b ? 1 : 0; });
            }
            return arr;
          }
          if (v && typeof v === 'object') {
            var out = {};
            Object.keys(v).sort().forEach(function (k) {
              var set = inSet || SET_KEY.test(k);
              if (set && isEmptyVal(v[k])) return;
              if (v[k] === undefined) return;
              out[k] = canon(v[k], set);
            });
            return out;
          }
          return v;
        }
        function sigOf(state) {
          var o = {};
          Object.keys(state || {}).forEach(function (k) { if (k !== 'page') o[k] = state[k]; });
          try { return path + '|' + JSON.stringify(canon(o, false)); } catch (e) { return null; }
        }
        try {
          var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
          var ss = store();
          if (nav && nav.type === 'back_forward' && ss) {
            var saved = JSON.parse(ss.getItem(KEY) || 'null');
            if (saved && saved.sig && saved.hits && saved.sig.indexOf(path + '|') === 0) mem = saved;
          }
        } catch (e) { mem = null; }
        return {
          read: function (o) {
            var sig = sigOf(o && o.state);
            return mem && sig && mem.sig === sig ? mem.hits : null;
          },
          write: function (o) {
            var sig = sigOf(o && o.state);
            if (!sig) return;
            mem = { sig: sig, hits: o.hits };
            var ss = store();
            if (!ss) return;
            try { ss.setItem(KEY, JSON.stringify(mem)); } catch (e) { try { ss.removeItem(KEY); } catch (e2) {} }
          },
        };
      }

      // Overlay-Suchfeld: an dieselbe Query gebunden (connectSearchBox). Der Input
      // liegt im Overlay-Kopf (vom Overlay-Controller synchron erzeugt, bevor die
      // Widgets mounten). Listener wird einmalig verdrahtet; Wert wird gespiegelt,
      // solange der Nutzer nicht gerade darin tippt.
      var overlaySearchWidget = instantsearch.connectors.connectSearchBox(function(renderOptions) {
        var input = document.querySelector('.ts-overlay-search-input');
        if (!input) return;
        if (!input.__tsWired) {
          input.__tsWired = true;
          var onType = function() { renderOptions.refine(input.value); };
          input.addEventListener('input', onType);
          input.addEventListener('search', onType); // native Clear-(x) feuert 'search'
        }
        if (document.activeElement !== input) input.value = renderOptions.query || '';
      });

      // Widgets in fester Reihenfolge zusammenstellen. searchBox + Kategorie-Baum
      // sind cfg-gesteuert (collection kann beides abschalten); fuer /search sind
      // alle Bedingungen true -> exakt die bisherige Widget-Liste/-Reihenfolge.
      var widgets = [];
      widgets.push(instantsearch.widgets.configure({
        hitsPerPage: cfg.hitsPerPage,
      }));

      if (cfg.searchBox.enabled) {
        widgets.push(instantsearch.widgets.searchBox({
          container: '#' + ID.searchbox,
          placeholder: cfg.searchBox.placeholder,
          autofocus: cfg.searchBox.autofocus,
          searchAsYouType: cfg.searchBox.searchAsYouType,
          cssClasses: {
            root: 'ts-searchbox-root',
          },
        }));

        // Zweites, an DIESELBE Query gebundenes Suchfeld im Mobile-Overlay
        // (connectSearchBox). Tippen dort verfeinert dieselben Ergebnisse live und
        // bleibt mit der Haupt-Suchleiste synchron. Filter/Routing unberuehrt.
        widgets.push(overlaySearchWidget({}));
      }

      widgets.push(instantsearch.widgets.stats({
        container: '#' + ID.stats,
        templates: {
          text: function(data) {
            return data.nbHits + ' Ergebnis' + (data.nbHits !== 1 ? 'se' : '') + ' in ' + data.processingTimeMS + 'ms';
          },
        },
      }));

      widgets.push(instantsearch.widgets.clearRefinements({
        container: '#' + ID.clearFilters,
        templates: {
          resetLabel: 'Filter zurücksetzen',
        },
      }));

      // Kategorie-Baum (category + subcategory in EINEM Widget, siehe oben)
      if (cfg.showCategoryTree) {
        widgets.push(categoryTreeWidget({
          attribute: 'category',
          sortBy: ['count:desc'],
          limit: 20,
        }));
        widgets.push(subsubcategoryTreeWidget({
          attribute: 'subsubcategory',
          sortBy: ['count:desc', 'name:asc'],
          limit: 50,
        }));
        widgets.push(subcategoryTreeWidget({
          attribute: 'subcategory',
          // STABILE Reihenfolge: kein 'isRefined' -> ausgewaehlte Eintraege
          // springen NICHT nach oben. Disjunctive Counts aendern sich beim
          // Auswaehlen nicht, daher bleibt die Liste ruhig unter dem Finger.
          sortBy: ['count:desc', 'name:asc'],
          limit: 50,
        }));
      }

      widgets.push(facetPanel('Marke')(instantsearch.widgets.refinementList)({
        container: '#' + ID.filterBrand,
        attribute: 'brand',
        searchable: true,
        searchablePlaceholder: 'Marke suchen...',
        // Pin ausgewaehlter Marken + stabile Ordnung (siehe DEFAULT_REFINEMENT_SORT)
        sortBy: DEFAULT_REFINEMENT_SORT,
        limit: 10,
        showMore: true,
        templates: { showMoreText: function(data) { return data.isShowingMore ? 'Weniger anzeigen' : 'Mehr anzeigen'; } },
      }));

      widgets.push(customPriceWidget({
        attribute: 'price',
        precision: 2,
      }));

      widgets.push(facetPanel('Lieferzeit')(instantsearch.widgets.refinementList)({
        container: '#' + ID.filterDelivery,
        attribute: 'availability',
        transformItems: function(items) {
          var order = { sofort: 0, bestellbar: 1, ausverkauft: 2 };
          var labels = {
            sofort: 'Auf Lager (3-5 Werktage)',
            bestellbar: 'Bestellbar (6-9 Wochen)',
            ausverkauft: 'Ausverkauft',
          };
          return items.slice().sort(function(a, b) {
            return (order[a.value] != null ? order[a.value] : 9) - (order[b.value] != null ? order[b.value] : 9);
          }).map(function(item) {
            return Object.assign({}, item, { label: labels[item.value] || item.label });
          });
        },
      }));

      widgets.push(activeChipsWidget({}));

      widgets.push(instantsearch.widgets.sortBy({
        container: '#' + ID.sortBy,
        items: cfg.sortItems,
      }));

      widgets.push(customHitsWidget({ cache: createBackNavHitsCache() }));

      search.addWidgets(widgets);

      // Kontextuelle Facetten (aus CONTEXT_FACETS generiert) registrieren
      if (contextWidgets.length) search.addWidgets(contextWidgets);

      // "Mehr laden"-UI (Fortschritt + Button) einmalig aufbauen
      setupLoadMoreUI();

      search.start();

      // Fehlerzustand: Typesense-Fehler/Timeout -> Skeleton weg, Fehlermeldung +
      // Retry (laeuft aktuelle Suche neu) + nativer Suchlink. Load-More-State
      // zuruecksetzen, damit "Mehr laden" nicht einfriert. searchErrored wird beim
      // naechsten erfolgreichen Render (renderHitsWidget) wieder geloescht.
      search.on('error', function () {
        // Fehler beim NACHLADEN (Karten stehen schon): Raster und Stapel
        // behalten, die fehlende Seite automatisch neu anfordern (Backoff).
        // Kein Fehler-Kasten, kein Rueckfall auf das SSR-Raster.
        if (loadMorePending && Object.keys(hitsNodes).length) {
          noteLoadMoreFailure();
          loadMorePending = false;
          scheduleLoadMoreRetry();
          try { updateLoadMoreUI(); } catch (e) {}
          return;
        }
        clearLoadMoreFailure();
        searchErrored = true;
        batchBusy = false;
        loadMorePending = false;
        pendingBatchPages = 0;
        hitsNodes = {}; // sauberer Neuaufbau nach erfolgreichem Retry
        try { updateLoadMoreUI(); } catch (e) {}
        // ★H2 (collection): Faellt Typesense NACH bereits erfolgtem Takeover aus,
        // nicht in einer Fehler-Box stranden (das native Raster waere versteckt) —
        // stattdessen zum SSR-Raster zuruecktauschen. Ein erfolgreicher Retry-Render
        // uebernimmt danach wieder automatisch. Fuer /search (kein revealTsUiOnce,
        // tsUiRevealed bleibt false) unveraendert -> normaler Fehlerzustand.
        if (tsUiRevealed && cfg.ssrFallbackSelector) {
          restoreSsrFallback();
        } else {
          buildErrorState(function () { try { if (search.helper) search.helper.search(); } catch (e2) {} });
        }
      });

      // Kinderlose Kategorien als Blaetter markieren (siehe precomputeCatHasSub)
      if (cfg.showCategoryTree) precomputeCatHasSub();

      // Mobile-Ergebnisansicht: "alle zuruecksetzen"-Link -> gleicher Pfad wie der
      // Overlay-Reset (clearRefinements).
      var mobileResetBtn = document.getElementById(ID.mobileReset);
      if (mobileResetBtn) {
        mobileResetBtn.addEventListener('click', function() {
          try { search.helper.clearRefinements().search(); } catch (e) {}
        });
      }

      // Zaehlt aktive Verfeinerungen pro Attributgruppe
      function countRefinements(state, attrs, isPrice) {
        var n = 0;
        if (!state) return 0;
        if (isPrice) {
          var num = (state.numericRefinements || {}).price;
          if (num && Object.keys(num).some(function(op) { return (num[op] || []).length; })) n = 1;
          return n;
        }
        var d = state.disjunctiveFacetsRefinements || {};
        (attrs || []).forEach(function(a) { n += (d[a] || []).length; });
        return n;
      }

      // Abschnitts-Zaehler (Badge im jeweiligen Facetten-Header)
      var SECTION_COUNTS = [
        { sel: '#' + ID.filterCategory, attrs: ['category', 'subcategory', 'subsubcategory'] },
        { sel: '#' + ID.filterPrice, price: true },
        { sel: '#' + ID.filterBrand, attrs: ['brand'] },
        { sel: '#' + ID.filterDelivery, attrs: ['availability'] },
      ];
      // Kontext-Facetten-Badges aus CONTEXT_FACETS ableiten -> jede kuenftige
      // Kontext-Facette bekommt automatisch ihren Header-Zaehler (keine
      // Hardcodierung von #ts-filter-ctx-material).
      Object.keys(CONTEXT_FACETS).forEach(function(cat) {
        CONTEXT_FACETS[cat].forEach(function(fc) {
          SECTION_COUNTS.push({ sel: '#' + ID.filterCtxPrefix + fc.attribute, attrs: [fc.attribute] });
        });
      });

      function updateSectionCounts(state) {
        SECTION_COUNTS.forEach(function(sc) {
          var host = document.querySelector(sc.sel);
          if (!host) return;
          var header = host.querySelector('.ts-facet-header');
          if (!header) return;
          var n = countRefinements(state, sc.attrs, sc.price);
          var badge = header.querySelector('.ts-facet-count');
          if (n > 0) {
            if (!badge) { badge = document.createElement('span'); badge.className = 'ts-facet-count'; header.appendChild(badge); }
            badge.textContent = String(n);
          } else if (badge) {
            badge.parentNode.removeChild(badge);
          }
        });
      }

      // Refinement-vs-Load-More-Erkennung fuer den Auto-Scroll: Signatur aus
      // Query + Refinements + Sortier-Index OHNE page. Load-More aendert nur die
      // page -> Signatur bleibt gleich -> KEIN Scroll. Nach Armierung (nach dem
      // initialen Laden inkl. URL-Restore) loest jede Signaturaenderung den
      // Scroll aus - aber NICHT solange das Overlay offen ist (dort scrollt erst
      // "Ergebnisse anzeigen").
      var prevSearchSig;
      var scrollArmed = false;
      setTimeout(function() { scrollArmed = true; }, 1500);
      function searchSignature(st) {
        if (!st) return '';
        return JSON.stringify({
          q: st.query || '',
          idx: st.index || '',
          d: st.disjunctiveFacetsRefinements || {},
          f: st.facetsRefinements || {},
          h: st.hierarchicalFacetsRefinements || {},
          num: st.numericRefinements || {},
        });
      }

      search.on('render', function() {
        try {
          var st = search.helper && search.helper.state;

          // Auto-Scroll bei echter Filter-/Query-/Sortieraenderung (nicht bei
          // Load-More, nicht beim Initial-Load, nicht bei offenem Overlay).
          var sig = searchSignature(st);
          var sigChanged = sig !== prevSearchSig;
          // Neue Trefferliste (Signatur ohne page) -> Auto-Load-Budget zuruecksetzen,
          // damit jede frische Ergebnismenge wieder ihre MAX_AUTOLOAD Seiten bekommt.
          // Load-More aendert die Signatur NICHT -> Budget bleibt/zaehlt weiter.
          // Ausserdem einen laufenden manuellen Stapel abbrechen.
          if (sigChanged) {
            autoLoadCount = 0;
            pendingBatchPages = 0;
            batchBusy = false;
            prevChainCount = 0;
            prevChainSig = episodeSig(st);
            // Neue Trefferliste (Sortierung/Filter/Query): laufende Fehler-
            // Episode samt Retry-Timer sofort verwerfen.
            if (loadMoreFailed && failSig !== prevChainSig) clearLoadMoreFailure();
            // L1: nach der ERSTEN echten Nutzer-Verfeinerung (nicht Initial-Load)
            // die CLS-min-height-Reserve loesen -> kein Whitespace unter kuerzeren
            // gefilterten Ergebnissen. User-initiiert -> die Layout-Aenderung faellt
            // in das Interaktions-Fenster, zaehlt also nicht als CLS. /search hat
            // keinen revealSelector -> No-op.
            if (prevSearchSig !== undefined && cfg.revealSelector) {
              var rvEl = document.querySelector(cfg.revealSelector);
              if (rvEl) rvEl.style.minHeight = '';
            }
          }
          if (scrollArmed && prevSearchSig !== undefined && sigChanged
              && !document.body.classList.contains('ts-filters-open')) {
            scrollToResultsTop();
          }
          prevSearchSig = sig;

          // Manuellen Stapel weiterladen: naechste Seite erst NACH dem Render der
          // vorigen (jede showMore-Kette wartet auf ihren Render -> keine Luecken).
          // Laeuft nur bei einem manuellen Klick (pendingBatchPages/batchBusy);
          // Load-More/Auto-Load aendern die Signatur nicht -> kein Scroll, keine
          // Auto-Load-Reaktivierung. Deferred (setTimeout 0), um Reentrancy im
          // Render-Zyklus zu vermeiden.
          if (!searchIdle() || loadMoreFailed) {
            // Zwischen-Render (Suche laeuft noch) oder Nachladen fehlgeschlagen
            // (Retry laeuft): Stapel-Zustand unveraendert lassen, die naechste
            // Seite folgt erst auf den fertigen Render.
          } else if (!infState.isFirst && infState.showPrevious && !loadMorePending && infState.loaded > 0
              && (prevChainSig !== episodeSig(st) || prevChainCount < PREV_CHAIN_MAX)) {
            // Liste beginnt nicht bei Seite 1 (Einstieg mit page in der URL ohne
            // Cache, z. B. /search?page=3 oder Zurueck ohne gespeicherte Seiten):
            // fehlende Vorseiten nacheinander laden, bis die Liste vollstaendig
            // ist. showPrevious aendert die URL nicht. Harte Grenze je Liste
            // (PREV_CHAIN_MAX), Fehler laufen ueber die Retry-Episode.
            if (prevChainSig !== episodeSig(st)) { prevChainSig = episodeSig(st); prevChainCount = 0; }
            prevChainCount++;
            loadMorePending = true;
            updateLoadMoreUI();
            var sp = infState.showPrevious;
            setTimeout(function () { runLoad('prev', sp); }, 0);
          } else if (pendingBatchPages > 0 && !infState.isLast && infState.showMore) {
            pendingBatchPages--;
            loadMorePending = true;
            var sm = infState.showMore;
            setTimeout(function () { runLoad('more', sm); }, 0);
          } else {
            // Stapel fertig, per isLast gestoppt oder per Verfeinerung abgebrochen
            // -> Ladezustand zuruecksetzen und Button-Zustand immer refreshen.
            if (batchBusy) batchBusy = false;
            pendingBatchPages = 0;
            updateLoadMoreUI();
          }

          var n = 0;
          if (st) {
            var d = st.disjunctiveFacetsRefinements || {};
            Object.keys(d).forEach(function(k) { n += (d[k] || []).length; });
            var num = (st.numericRefinements || {}).price;
            if (num && Object.keys(num).some(function(op) { return (num[op] || []).length; })) n += 1;
          }
          var badge = document.getElementById(ID.filterActiveCount);
          if (badge) {
            badge.textContent = String(n);
            badge.classList.toggle('is-visible', n > 0);
          }

          // Desktop-Reset-Button zeigt die Anzahl aktiver Filter (gleiche Logik
          // wie das Mobile-Badge). InstantSearch rendert den Button-Text jeden
          // Zyklus neu -> hier NACH dem Widget-Render ueberschreiben.
          var clearBtn = document.querySelector('#' + ID.clearFilters + ' .ais-ClearRefinements-button');
          if (clearBtn) clearBtn.textContent = n > 0 ? 'Filter zurücksetzen (' + n + ')' : 'Filter zurücksetzen';

          // Mobile-Fussleiste: Reset-Button nur bei aktiven Filtern zeigen, mit
          // Anzahl (gleiche Zaehllogik wie oben). Primaer-CTA bleibt immer.
          var resetCount = document.querySelector('.ts-filters-ov-reset-count');
          if (resetCount) resetCount.textContent = n > 0 ? '(' + n + ')' : '';
          var resetBtn = document.querySelector('.ts-filters-ov-reset');
          if (resetBtn) resetBtn.hidden = n === 0;

          // Mobile-Ergebnisansicht: dezenter "alle zuruecksetzen"-Link unter dem
          // Filter-Button, nur bei aktiven Filtern.
          var mReset = document.getElementById(ID.mobileReset);
          if (mReset) {
            mReset.hidden = n === 0;
            var mc = mReset.querySelector('.ts-mobile-reset-count');
            if (mc) mc.textContent = n > 0 ? '(' + n + ')' : '';
          }

          // Schwebender Filter-Button (mobil): Badge mit Filteranzahl
          var floatBadge = document.querySelector('.ts-floating-filter .ts-floating-filter-badge');
          if (floatBadge) {
            floatBadge.textContent = String(n);
            floatBadge.classList.toggle('is-visible', n > 0);
          }

          updateSectionCounts(st);

          // Sticky-Leiste: Live-Trefferzahl im "Ergebnisse anzeigen"-Button
          var res = search.helper && search.helper.lastResults;
          var nb = res ? res.nbHits : null;
          var cEl = document.querySelector('.ts-filters-ov-count');
          if (cEl && nb != null) cEl.textContent = '(' + nb + ')';

          // Dezenter Lade-Zustand waehrend haengender Suchen (kein Blank-Flash;
          // alte Treffer bleiben sichtbar, nur leicht gedimmt).
          var resultsEl = document.querySelector(SEL.results);
          if (resultsEl) resultsEl.classList.toggle('is-searching', search.status === 'stalled');

          // Skeleton entfernen, sobald echte Ergebnisse vorliegen
          if (res && typeof res.nbHits === 'number') {
            var sk = document.getElementById(ID.hitsSkeleton);
            if (sk && sk.parentNode) sk.parentNode.removeChild(sk);
          }

          // Auto-Load-Nachpruefung nach dem Layout (Button evtl. weiter in Sicht
          // auf kurzen Seiten -> naechsten Auto-Load ausloesen, bis MAX/isLast).
          window.requestAnimationFrame(maybeAutoLoad);
        } catch (e) {}
      });

      // denk-collect: Suchbegriff-Tracking (Merchandising-Signal). InstantSearch
      // ist AJAX, trifft also nie /search?q= — daher hier direkt am Renderzyklus.
      // 1,5s-Debounce nach dem letzten Tastendruck, nur finale Begriffe (>= 2
      // Zeichen), Duplikate unterdrückt. Läuft über denk.track: anonym essential
      // ohne Einwilligung, identifiziert mit; PII-Guards (E-Mail/Telefonnummer)
      // greifen client- UND serverseitig. cfg.tracking.searchTermEvent=null => aus.
      if (cfg.tracking.searchTermEvent) (function () {
        var lastSent = null;
        var timer = null;
        var timerQ = null;
        // Event erst, wenn die Ergebnisse ZUM aktuellen Begriff da sind: der
        // Debounce startet nur bei status 'idle' und Ergebnissen, deren
        // Request-State denselben Begriff traegt. Lade-Renders (status
        // 'loading'/'stalled') starten keinen Timer; ein Lade-Render mit neuem
        // Begriff bricht einen laufenden Timer ab (Tippen geht weiter). Antwortet
        // Typesense langsamer als der Debounce, kommt das Event eben spaeter,
        // aber genau einmal und mit der richtigen Trefferzahl.
        function resultsQuery(results) {
          if (!results) return null;
          var rq = results._state && typeof results._state.query === 'string' ? results._state.query
            : (typeof results.query === 'string' ? results.query : null);
          return rq == null ? null : rq.trim();
        }
        search.on('render', function () {
          try {
            var q = (search.helper && search.helper.state.query || '').trim();
            if (timer && timerQ !== q) { clearTimeout(timer); timer = null; timerQ = null; }
            if (search.status !== 'idle') return;
            var results = search.helper && search.helper.lastResults;
            if (!results || resultsQuery(results) !== q) return;
            var n = typeof results.nbHits === 'number' ? results.nbHits : null;
            if (timer) { clearTimeout(timer); timer = null; }
            if (q.length < 2 || q === lastSent) return;
            timerQ = q;
            timer = setTimeout(function () {
              timer = null; timerQ = null;
              if (!window.denk || typeof window.denk.track !== 'function') return;
              var cur = (search.helper && search.helper.state.query || '').trim();
              if (cur !== q || q === lastSent) return;
              lastSent = q;
              var props = { search_term: q.slice(0, 100), path: cfg.tracking.path };
              if (typeof n === 'number') props.num_items = n;
              window.denk.track(cfg.tracking.searchTermEvent, props);
            }, 1500);
          } catch (e) {}
        });
      })();

      // denk-collect: search_result_click. Misst, WELCHES Ergebnis auf WELCHER
      // Position geklickt wird (Ranking-Qualitaet, Zero-Click-Analyse). Delegierter
      // Listener am Ergebniscontainer, feuert erst beim echten Klick. Position ist
      // 1-basiert ueber Seitenoffset (page * hitsPerPage) plus Index in der aktuell
      // gerenderten Trefferliste. Begriffsfeld heisst wie beim search-Event
      // (search_term), PII-Guard greift server-/clientseitig. Ist denk noch nicht
      // geladen, passiert einfach nichts. try/catch, keine Layout-Aenderung.
      (function () {
        var hitsEl = document.getElementById(ID.hits);
        if (!hitsEl) return;
        hitsEl.addEventListener('click', function (ev) {
          try {
            // Karte ist kein <a>-Wrapper mehr: Klick auf einen der inneren
            // Produkt-Links (Bild/Titel) zaehlt, Position ueber die .ts-card.
            var link = ev.target && ev.target.closest ? ev.target.closest('.ts-card a[href]') : null;
            if (!link || !hitsEl.contains(link)) return;
            if (!window.denk || typeof window.denk.track !== 'function') return;
            var card = link.closest('.ts-card');
            var cards = hitsEl.querySelectorAll('.ts-card');
            var idx = Array.prototype.indexOf.call(cards, card);
            if (idx < 0) return;
            var st = search.helper && search.helper.state;
            var page = st && typeof st.page === 'number' ? st.page : 0;
            var perPage = st && typeof st.hitsPerPage === 'number' ? st.hitsPerPage : 24;
            var q = ((st && st.query) || '').trim();
            var props = { pos: page * perPage + idx + 1, target: link.getAttribute('href') || '', path: cfg.tracking.path };
            if (q) props.search_term = q.slice(0, 100);
            window.denk.track(cfg.tracking.event, props);
          } catch (e) {}
        });
      })();
    });

    // ---- Mobile: Vollbild-Overlay + Accordions + Sticky-Ergebnisleiste --------
    // Desktop bleibt die statische Sidebar (Overlay/Accordion nur unter dem
    // Breakpoint via matchMedia + CSS-Media-Query).
    (function () {
      var MOBILE_MQ = window.matchMedia('(max-width: 1024px)');
      var toggleBtn = document.getElementById(ID.filterToggle);
      var filtersPanel = document.getElementById(ID.filtersPanel);
      if (!toggleBtn || !filtersPanel) return;

      // Bestehende Facettenbloecke in einen scrollbaren Wrapper verschieben,
      // damit Kopf + Sticky-Leiste im Overlay fixiert bleiben. Der Wrapper ist
      // auf dem Desktop via display:contents layout-neutral.
      var ovScroll = document.createElement('div');
      ovScroll.className = 'ts-filters-ov-scroll';
      while (filtersPanel.firstChild) { ovScroll.appendChild(filtersPanel.firstChild); }
      filtersPanel.appendChild(ovScroll);

      // Overlay-Kopf (Titel + Schliessen) und Sticky-Leiste injizieren
      var ovHeader = document.createElement('div');
      ovHeader.className = 'ts-filters-ov-header';
      ovHeader.innerHTML = '<div class="ts-filters-ov-headrow">'
        +   '<span class="ts-filters-ov-title">Filter</span>'
        +   '<button type="button" class="ts-filters-ov-close" aria-label="Filter schließen">'
        +     '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 6l12 12M6 18L18 6"/></svg>'
        +   '</button>'
        + '</div>'
        // ★H1: Das Overlay-Suchfeld NUR einfuegen, wenn die Suchleiste aktiv ist
        // (searchBox.enabled). Auf Collection-Seiten ist sie AUS -> sonst erschiene
        // ein prominentes, funktionsloses "Suche verfeinern…"-Feld (kein Widget,
        // das refine() aufruft) = wirkt kaputt.
        + (cfg.searchBox.enabled
            ? '<div class="ts-overlay-search">'
              +   '<svg class="ts-overlay-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>'
              +   '<input type="search" class="ts-overlay-search-input" placeholder="Produkte, Marken, SKU suchen..." autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" aria-label="Produkte, Marken, SKU suchen">'
              + '</div>'
            : '');
      filtersPanel.insertBefore(ovHeader, filtersPanel.firstChild);

      // Reihenfolge (von oben): sekundaerer Reset-Button (mit Filteranzahl,
      // nur sichtbar wenn Filter aktiv), darunter die grosse Vollbreiten-CTA
      // am unteren Rand (Daumenreichweite).
      var ovBar = document.createElement('div');
      ovBar.className = 'ts-filters-ov-bar';
      ovBar.innerHTML = '<button type="button" class="ts-filters-ov-reset" hidden>Filter zurücksetzen <span class="ts-filters-ov-reset-count"></span></button>'
        + '<button type="button" class="ts-filters-ov-apply">Ergebnisse anzeigen <span class="ts-filters-ov-count"></span></button>';
      filtersPanel.appendChild(ovBar);

      function applyAccordionDefaults() {
        if (!MOBILE_MQ.matches) return;
        if (filtersPanel.getAttribute('data-acc-init') === '1') return;
        filtersPanel.setAttribute('data-acc-init', '1');
        // Alle Abschnitte zu, ausser Kategorie
        var headers = filtersPanel.querySelectorAll('.ts-facet-header');
        Array.prototype.forEach.call(headers, function (h) {
          var section = h.parentElement;
          if (section && section.id !== ID.filterCategory) section.classList.add('is-collapsed');
        });
      }

      // Portal-Anker: merkt sich die urspruengliche Position der Sidebar, damit
      // wir sie beim Oeffnen nach <body> verschieben (raus aus transformierten
      // Vorfahren -> fixed deckt wirklich den Viewport) und beim Schliessen
      // exakt wieder einsetzen koennen.
      var portalAnchor = document.createComment('ts-filters-anchor');

      function portalToBody() {
        if (filtersPanel.parentNode === document.body) return;
        filtersPanel.parentNode.insertBefore(portalAnchor, filtersPanel);
        document.body.appendChild(filtersPanel);
      }
      function restoreFromBody() {
        if (!portalAnchor.parentNode) return;
        portalAnchor.parentNode.insertBefore(filtersPanel, portalAnchor);
        portalAnchor.parentNode.removeChild(portalAnchor);
      }

      // ---- A11y: Fokus-Falle + Hintergrund inert + Fokus-Rueckgabe -----------
      var lastFocusTrigger = null;
      function focusableIn(root) {
        var sel = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        return Array.prototype.filter.call(root.querySelectorAll(sel), function (n) {
          return n.getClientRects().length > 0; // sichtbar (kein display:none / [hidden])
        });
      }
      // Alles ausser dem Overlay fuer Tastatur/AT ausblenden (inert + aria-hidden),
      // damit Tab und Screenreader im Overlay gefangen bleiben.
      function setBackgroundInert(on) {
        Array.prototype.forEach.call(document.body.children, function (el) {
          if (el === filtersPanel) return;
          if (on) { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); }
          else { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
        });
      }

      function openFilters(trigger) {
        lastFocusTrigger = trigger || document.activeElement || toggleBtn;
        portalToBody();
        filtersPanel.classList.add('is-open');
        document.body.classList.add('ts-filters-open');
        toggleBtn.setAttribute('aria-expanded', 'true');
        setBackgroundInert(true);
        applyAccordionDefaults();
        // Overlay-Suchfeld beim Oeffnen mit der aktuellen Query synchronisieren
        try {
          var oi = ovHeader.querySelector('.ts-overlay-search-input');
          if (oi && window.__tsSearchInstance && window.__tsSearchInstance.helper) {
            oi.value = window.__tsSearchInstance.helper.state.query || '';
          }
        } catch (e) {}
        // Fokus ins Overlay (Schliessen-Button) verschieben
        var closeBtn = ovHeader.querySelector('.ts-filters-ov-close');
        if (closeBtn) closeBtn.focus();
      }
      function closeFilters() {
        filtersPanel.classList.remove('is-open');
        document.body.classList.remove('ts-filters-open');
        toggleBtn.setAttribute('aria-expanded', 'false');
        setBackgroundInert(false);
        restoreFromBody();
        // Fokus zurueck zum ausloesenden Element
        if (lastFocusTrigger && typeof lastFocusTrigger.focus === 'function') {
          try { lastFocusTrigger.focus(); } catch (e) {}
        }
        lastFocusTrigger = null;
      }

      // Tab-Falle: innerhalb des offenen Overlays zyklisch (erstes <-> letztes)
      filtersPanel.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || !filtersPanel.classList.contains('is-open')) return;
        var f = focusableIn(filtersPanel);
        if (!f.length) { e.preventDefault(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      toggleBtn.addEventListener('click', function () {
        if (filtersPanel.classList.contains('is-open')) closeFilters(); else openFilters(toggleBtn);
      });
      ovHeader.querySelector('.ts-filters-ov-close').addEventListener('click', closeFilters);
      // "Ergebnisse anzeigen": Overlay schliessen und oben bei den Treffern landen.
      ovBar.querySelector('.ts-filters-ov-apply').addEventListener('click', function () {
        closeFilters();
        scrollToResultsTop(); // deferred + instant intern -> landet zuverlaessig oben
      });
      ovBar.querySelector('.ts-filters-ov-reset').addEventListener('click', function () {
        try {
          if (window.__tsSearchInstance && window.__tsSearchInstance.helper) {
            window.__tsSearchInstance.helper.clearRefinements().search();
          }
        } catch (e) {}
      });

      // Accordion: Klick auf einen Facetten-Header klappt seinen Abschnitt (mobil)
      filtersPanel.addEventListener('click', function (e) {
        if (!MOBILE_MQ.matches) return;
        var header = e.target.closest ? e.target.closest('.ts-facet-header') : null;
        if (!header || header.parentElement === filtersPanel) return;
        if (!filtersPanel.contains(header)) return;
        header.parentElement.classList.toggle('is-collapsed');
      });

      // Beim Wechsel auf Desktop das Overlay sicher schliessen
      (MOBILE_MQ.addEventListener ? MOBILE_MQ.addEventListener.bind(MOBILE_MQ, 'change') : MOBILE_MQ.addListener.bind(MOBILE_MQ))(function (ev) {
        if (!ev.matches) closeFilters();
      });

      // Escape schliesst das Overlay
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && filtersPanel.classList.contains('is-open')) closeFilters();
      });

      // ---- Schwebender Filter-Button (mobil) --------------------------------
      // Erscheint, sobald der inline "Filter"-Trigger aus dem Viewport scrollt;
      // oeffnet DASSELBE Overlay; traegt das Aktiv-Badge. Position: unten MITTE
      // (Chat unten-links, Scroll-nach-oben unten-rechts -> keine Ueberlappung).
      var floatFilter = document.createElement('button');
      floatFilter.type = 'button';
      floatFilter.className = 'ts-floating-filter';
      floatFilter.setAttribute('aria-label', 'Filter öffnen');
      floatFilter.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="10" y2="18"/></svg>'
        + '<span>Filter</span>'
        + '<span class="ts-floating-filter-badge"></span>';
      document.body.appendChild(floatFilter);
      floatFilter.addEventListener('click', function () { openFilters(floatFilter); });

      if ('IntersectionObserver' in window) {
        var triggerObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            // sichtbar, wenn der inline Trigger NICHT mehr im Viewport ist
            floatFilter.classList.toggle('is-visible', !e.isIntersecting);
          });
        }, { threshold: 0 });
        triggerObserver.observe(toggleBtn);
      }

      // ---- Nach-oben-Button (beide Viewports) -------------------------------
      // Unten RECHTS, erscheint nach einem Scroll-Schwellwert, sanfter Scroll.
      var scrollTop = document.createElement('button');
      scrollTop.type = 'button';
      scrollTop.className = 'ts-scroll-top';
      scrollTop.setAttribute('aria-label', 'Nach oben');
      scrollTop.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
      document.body.appendChild(scrollTop);
      scrollTop.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      var scrollTicking = false;
      function onScroll() {
        if (scrollTicking) return;
        scrollTicking = true;
        window.requestAnimationFrame(function () {
          scrollTop.classList.toggle('is-visible', window.pageYOffset > 600);
          scrollTicking = false;
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    })();
  };

  // Reine Helfer fuer Tests (assets/ts-browse-core.test.mjs).
  window.initTsBrowse.filterClause = tsFilterClause;
  window.initTsBrowse.injectFilters = tsInjectFilters;
})();
